import { z } from "zod";
import { zPlainText, zPlainTextRequired, zSafeHtml } from "../middleware/zod-sanitize.js";
import { formCategories, SECTION_TYPE_TABLE_SECTION } from "./../../helpers/index.js";
import { MAX_FILE_KEYS_PER_REMOVAL, templateEditCases } from "./forms.constant.js";
import {
  objectIdString,
  uuidStorageKey,
  filterWhereClause,
  filterWhereClauseFor,
  sortConfigFor,
  dynamicCustomFieldPatterns,
  sortConfig,
  extendStatusResponse,
} from "../../helpers/schemas/common.schema.js";

// Per-endpoint filterable-column allow-lists. Each grid below builds a field-restricted
// `where` from these (plus dynamic custom-field / form-question column patterns) instead of
// the OPEN shared `filterWhereClause`, so a client can only filter on columns the grid
// actually renders. `gridValueSchema` (shared) keeps the open clause for endpoints not yet
// restricted; restricted endpoints override `where` via a per-endpoint value schema.
const templateTypesListFilterableFields = ["name", "description", "createdDate", "createdBy"] as const;
const templateTypesListWhere = filterWhereClauseFor(templateTypesListFilterableFields, {
  patterns: dynamicCustomFieldPatterns,
});

const templatesListFilterableFields = [
  "name",
  "description",
  "type.name",
  "createdDate",
  "createdBy.fullName",
  "_id",
  "isArchived",
  // Programmatic base filters (form-template pickers scope by project / type)
  "projectId",
  "type._id",
] as const;
const templatesListWhere = filterWhereClauseFor(templatesListFilterableFields, {
  patterns: dynamicCustomFieldPatterns,
});

const tipfListFilterableFields = [
  "counter",
  "title",
  "description",
  "space.spaceName",
  "status",
  "selectedForm.name",
  "selectedForm.type.name",
  "selectedFormFlow.name",
  "createdBy.fullName",
  "currentLevelName",
  "createdDate",
  "dueDate",
  "lastAction.userName",
  "lastAction.actionName",
  "lastAction.timeStamp",
  "currentLevelOfForm",
  "isArchived.status",
  "isApproved",
  "_id",
] as const;
const tipfListWhere = filterWhereClauseFor(tipfListFilterableFields, {
  patterns: dynamicCustomFieldPatterns,
});
// Sortable columns for the form-instances grid DIVERGE from its filterable set: the grid renders
// four extra sortable-only columns (all columns are sortable in view mode — see SlickGrid1 wrapper)
// that carry no `filterType`, so they are not in tipfListFilterableFields:
//   - formProgress            (client-computed progress column)
//   - contractName / contractType / contractOrgName ($addFields-derived AFTER the $sort stage)
// They must still be accepted by the sort allow-list or clicking those headers would 400. Sorting on
// them is a server-side no-op (field absent at the $sort stage) — no worse than the non-indexed
// columns already allowed. Superset = every filterable column (all sortable) UNION the four extras.
export const tipfListSortableFields = [
  ...tipfListFilterableFields,
  "formProgress",
  "contractName",
  "contractType",
  "contractOrgName",
] as const;
const tipfListSorted = sortConfigFor(tipfListSortableFields, { patterns: dynamicCustomFieldPatterns });

const libraryEntitiesFilterableFields = [
  "data.resourceType",
  "data.formCategory",
  "name",
  "selectedForm",
  "space.spaceName",
  "createdBy",
  "createdDate",
  "_id",
] as const;
const libraryEntitiesWhere = filterWhereClauseFor(libraryEntitiesFilterableFields, {
  patterns: dynamicCustomFieldPatterns,
});

const formFlowListFilterableFields = [
  "name",
  "selectedForm",
  "space.spaceName",
  "createdBy",
  "createdById",
  "createdDate",
  "_id",
] as const;
const formFlowListWhere = filterWhereClauseFor(formFlowListFilterableFields, {
  patterns: dynamicCustomFieldPatterns,
});

// Base response schema with result and errorMessage.
// RESPONSE-ONLY: this const (and every `*Response` derived from it below) is referenced solely as a
// route's `responseSchema:` in forms.controller.ts. `responseSchema` is never parsed at runtime
// (helpers/registerRoutes.ts mentions it only in a comment; helpers/routeTypes.ts uses it purely for
// the compile-time `service:`-route check, and every forms route is a `handler:` route), so nothing
// here can strip or reject a live payload.
//
// `result` stays `z.array(z.unknown())` rather than a concrete element type because ONE const backs
// 12 `*Response` exports whose payloads are mutually disjoint. The five that actually assign `result`:
//   * getTemplateTypes  → Template_types documents (forms.service.ts:493). cube-qa: 51478 docs, keys
//     _id:ObjectId=51478, createdDate:number=51478, name:string=51478, resourceType:string=51478,
//     portalId:ObjectId=51476 (null=2), updatedDate:number=51475, isDeleted/isUsedInForms=51387,
//     createdBy:object=51385 (null=2), description:string=51383, count/isDefault=859.
//   * getEntitesFromLibrary → `{...Library.data, _id, createdBy, createdDate}` (forms.service.ts:2462);
//     cube-qa Library holds two disjoint entity shapes, TEMPLATE_SECTION=56 and FORM_FLOW=8.
//   * getFormEntries/fetchFormEntries → extracted rows or raw InspectionAndProgress docs (6393/6395).
//   * getFormWBSItemsAndAssociatedBoqItems (forms.contracts.service.ts:2432) → WBS/BOQ rows.
//   * getContractFormBoqItems (forms.contracts.service.ts:3839) → contract BOQ rows.
// The remaining five (addSectionToLibrary, deleteSectionsFromLibrary, projectTemplateConfigHandler,
// getContractTaskHierarchy, formUserSettingsController) never assign `result` at all — hence
// `.optional()`.
// (`getFormTasks` was listed here too; that was wrong — contractsTimelineService.ts:790 does assign
// `output.result = data`. Its rows stay `z.unknown()` deliberately: the three consumers
// — formToPrint.tsx, taskContent.tsx and WBSContent.tsx — each declare a richer, hand-verified row
// type than this schema could express (taskContent's `boqItems` is typed from a component's props),
// so a row schema here would be less accurate than what they already have and would fight them.)
// Each shape is already modelled in forms/forms.collection.ts (templateTypeDocument) and
// forms/inspectionAndProgress.collection.ts; those files import FROM this one, so referencing them
// here would create an import cycle, and re-declaring them inline would duplicate a model that must
// not drift.
const formStatusResponse = extendStatusResponse({
  result: z.array(z.unknown()).optional(),
  count: z.number().nonnegative().optional(),
});

const gridValueSchema = z.object({
  skip: z.number().nonnegative().optional().meta({ description: "Pagination offset (rows to skip)." }),
  take: z.number().nonnegative().optional().meta({ description: "Pagination page size (max rows to return)." }),
  sorted: z.array(sortConfig).optional().meta({ description: "Grid sort configuration (column + direction)." }),
  where: filterWhereClause.meta({
    description: "Grid filter clause; overridden by a field-restricted allow-list in most list endpoints.",
  }),
  requiresCounts: z
    .boolean()
    .optional()
    .meta({ description: "If true, also returns the total matching-row count for pagination." }),
});

// Field types enum for question fields
export const fieldTypeEnum = z.enum([
  "single-line",
  "multi-line",
  "date-time",
  "image",
  "fileUpload",
  "single-select",
  "multi-select",
  "users",
  "look-up",
  "number",
  "url",
  "geoLocation",
  "text",
  "tableDataSource",
  "actualStartDate",
  "actualEndDate",
  "progress",
  "remDuration",
  "formula",
  "concatenate",
  "autoNumber",
]);

const answerSchema = z.object({
  value: z.unknown().optional().meta({
    description:
      "The stored answer for a question field. Shape depends on the field's fieldType (string for text/url, epoch-ms number for date-time, array for multi-select/users, awsKey/base64 metadata for image/fileUpload, number for number/formula/autoNumber).",
  }),
  createdDate: z
    .number()
    .optional()
    .meta({ description: "Epoch milliseconds when this answer was written; set server-side, not by the caller." }),
  createdBy: objectIdString
    .optional()
    .meta({ description: "Account _id (ObjectId 24-hex string) of the user who wrote this answer; set server-side." }),
});

/**
 * One entry of a stored `image` / `fileUpload` answer — the single declaration of that shape.
 *
 * `answerSchema.value` stays `z.unknown()` (the concrete shape follows each question's `fieldType`, and
 * tightening it needs the per-fieldType discriminated schema logged in the latent-bugs backlog), so this
 * is deliberately a *named shape* rather than a narrowing of `value`: nothing here changes what the
 * route accepts at runtime. It exists so the paths that genuinely read the file list — the server's
 * upload/remove handlers and the web renderers — share one declaration instead of three that drift.
 *
 * Two kinds of entry live in the same list, which is why every field is optional:
 * - an uploaded file owns an object in storage and carries `awsKey`;
 * - an entry picked out of the File Manager owns no object of its own and carries `fileVersion`
 *   (plus `fileId` / `resourceType` / `versionNumber`, which is how the viewer opens it).
 *
 * Its consumers, so a change here can be traced: the server's upload/remove handlers and
 * `FormOpResult.addedFileData` (forms.service.ts), `addedFileData` on
 * `taskInspectionAndProgressFormsControllerResponse` below, and the web renderers' `FieldFileValue`
 * (questionFieldsHelper.tsx), which is a bare alias of this type. That is two masters — the renderers
 * read the File-Manager fields, the server write path never produces them — so narrowing this shape
 * (e.g. into a union on `awsKey` vs `fileVersion`) moves both at once.
 */
export const formFileEntrySchema = z.object({
  awsKey: z.string().optional().meta({
    description:
      "Storage key of an uploaded file; minted server-side on upload. Absent for File-Manager entries. Fetch the bytes from /fileSystemServer/getAttachmentFile/:awsKey.",
  }),
  name: z.string().optional().meta({ description: "Display name of the entry, as rendered in the answer." }),
  type: z.string().optional().meta({ description: "MIME type of the entry." }),
  fileVersion: z
    .string()
    .optional()
    .meta({ description: "File Manager version id; present instead of awsKey for entries picked from files." }),
  fileId: z.string().optional().meta({ description: "File Manager file id, for opening the entry in the viewer." }),
  resourceType: z.string().optional().meta({ description: "File Manager resource type of the picked entry." }),
  versionNumber: z.number().optional().meta({ description: "File Manager version number of the picked entry." }),
});
export type FormFileEntry = z.infer<typeof formFileEntrySchema>;

const attachmentsDataSchema = z.object({
  awsKey: z
    .string()
    .optional()
    .meta({ description: "S3 object key of the uploaded attachment (as returned by uploadFilesInTemplate)." }),
  name: z.string().optional().meta({ description: "Display file name of the attachment." }),
  type: z.string().optional().meta({ description: "MIME type of the attachment, e.g. image/png or application/pdf." }),
});

/**
 * A formula field's expression must be plain arithmetic over the mapped numeric
 * field variables. We bound its length and restrict it to letters/digits (incl.
 * unicode variable names), whitespace and the arithmetic operators. This rejects
 * the structural characters used by ranges (`[1:1e9]`), matrices, block/assignment
 * expressions, and the factorial operator (`!`) — all of which can make client-side
 * evaluation unbounded in time/memory. This is the server-side defence-in-depth
 * layer; the frontend additionally restricts the parsed AST node types.
 */
const MAX_FORMULA_EXPRESSION_LENGTH = 1000;
const SAFE_FORMULA_EXPRESSION_REGEX = /^[\p{L}\p{N}_\s+\-*/%^().,]*$/u;
const safeFormulaExpression = z
  .string()
  .max(MAX_FORMULA_EXPRESSION_LENGTH)
  .refine((value) => SAFE_FORMULA_EXPRESSION_REGEX.test(value), {
    message: "Formula contains unsupported characters",
  });

export const questionFieldSchema = z
  .object({
    _id: z.string().length(36).meta({
      description:
        "Stable 36-char UUID (not a Mongo ObjectId) identifying this question within its section; used as the arrayFilter key when cascading answers/edits into live form instances. Reuse the same UUID to edit an existing question; generate a fresh one to add.",
    }),
    fieldType: fieldTypeEnum.meta({
      description:
        "The question's input/behaviour type, which drives how the answer is captured and validated. Special non-input types: formula (computed from other numeric fields), autoNumber (auto-incrementing counter), concatenate (joins other fields), look-up (Location/Teams), tableDataSource (grid), progress/remDuration/actualStartDate/actualEndDate (task-form fields).",
    }),
    fieldLabel: z.string().optional().meta({
      description:
        "Optional short CODE / identifier for the question (e.g. 'MBOOK_NO', 'SES_NO' — used by SAP and formula/variable mappings). This is NOT the text shown to users and is rarely set — leave it unset unless you specifically need a code key. The displayed question prompt is `questionValue`, not this.",
    }),
    questionValue: z.string().meta({
      description:
        "The human-readable question label/prompt shown to the user — THIS is the primary question text (e.g. 'Name of Work', 'Date of Test', 'Average'). Set it on EVERY question: a question with no questionValue renders with a blank/undefined label. (Despite the name, this is the label, not an answer value.)",
    }),
    fieldHeading: z.string().optional().meta({
      description:
        "The answer-type display name shown as the question's type chip (e.g. 'Single Line', 'Number', 'Date & Time', 'Look Up') — the human name of the chosen fieldType. Set it alongside questionValue to match the standard form-builder output.",
    }),
    isMandatory: z.boolean().optional().meta({
      description:
        "If true, the answer is required; enforced server-side by checkAllMandatoryFields before a form section can be submitted.",
    }),
    // MAILBOX/ISSUES visibility flags. `.optional()` because the vast majority of STORED questions
    // predate the toggles and are replayed back through this schema by the answer-save routes
    // (updateIssueCustomFields, the mailbox equivalent) — those carry no resourceType, so they cannot
    // be held to the rule and must keep parsing. On the template-authoring routes, where the
    // resourceType IS known, `fieldFlagsPresent` requires both (see §7.5 of forms/CLAUDE.md).
    editableInForm: z.boolean().optional().meta({
      description:
        "MAILBOX/ISSUES templates only: whether this question appears in the Create Mail / Create Issue form. Absent means true — a question authored before the flags existed, or imported from a Library section whose template type never carried them, is visible everywhere; only an explicit false hides it. REQUIRED on every question of a MAILBOX or ISSUES template (see the resourceType-scoped rules on saveTemplateSchema). Ignored for every other resourceType.",
    }),
    isMetadata: z.boolean().optional().meta({
      description:
        "MAILBOX/ISSUES templates only: whether this question appears in the mail / issue detail sidebar editor, and is validated as mandatory by the metadata-only save routes. Absent means true, same as editableInForm. REQUIRED on every question of a MAILBOX or ISSUES template. Ignored for every other resourceType.",
    }),
    answer: answerSchema.optional().meta({
      description:
        "Prefilled/default answer on a template is unusual; on a live form instance this holds the captured answer. Normally written via the updateTaskInspectionAndProgress answer path, not sent at template-build time.",
    }),
    allowPreDefinedColValues: z.boolean().optional().meta({
      description: "Table field config: whether predefined column values are allowed for a tableDataSource question.",
    }),
    answerTrack: z.record(z.string(), answerSchema).optional().meta({
      description:
        "Per-row/per-key answer history keyed by row or occurrence id; used for table/multi-instance questions. Managed server-side.",
    }),
    minFieldLimit: z
      .number()
      .optional()
      .nullable()
      .meta({ description: "Minimum allowed value/length for number or text fields; null = no minimum." }),
    maxFieldLimit: z
      .number()
      .optional()
      .nullable()
      .meta({ description: "Maximum allowed value/length for number or text fields; null = no maximum." }),
    placeholderText: z.string().optional().meta({ description: "Placeholder text shown in the empty input." }),
    selectionScope: z
      .enum([
        "futureDateTime",
        "pastDateTime",
        "rangeBased",
        "anyDateTime",
        "currentDateTime",
        "currentSection",
        "entireForm",
        "space",
        "project",
      ])
      .optional()
      .meta({
        description:
          "Constrains selectable values. For date-time fields: futureDateTime/pastDateTime/anyDateTime/currentDateTime/rangeBased. For look-up/reference fields: currentSection/entireForm/space/project scope of the lookup source.",
      }),
    minFieldRange: z
      .number()
      .optional()
      .nullable()
      .meta({ description: "For rangeBased date-time selectionScope: lower bound as epoch ms; null = unbounded." }),
    maxFieldRange: z
      .number()
      .optional()
      .nullable()
      .meta({ description: "For rangeBased date-time selectionScope: upper bound as epoch ms; null = unbounded." }),
    minNoOfFiles: z
      .number()
      .optional()
      .nullable()
      .meta({ description: "For image/fileUpload fields: minimum number of files required; null = no minimum." }),
    maxNoOfFiles: z
      .number()
      .optional()
      .nullable()
      .meta({ description: "For image/fileUpload fields: maximum number of files allowed; null = no maximum." }),
    answerChoices: z
      .array(z.string())
      .optional()
      .meta({ description: "The option list for single-select/multi-select fields." }),
    lookUpType: z.enum(["Location", "Teams"]).optional().meta({
      description:
        "For look-up fields: which entity set the lookup draws from — project Locations or Teams. Aggregated per template into Forms.lookUpUsed.",
    }),
    allowedFileTypes: z
      .array(z.enum(["image/jpg", "image/jpeg", "application/pdf", "image/png"]))
      .optional()
      .meta({
        description:
          "For image/fileUpload fields: permitted MIME types; empty array means no restriction. Must match the server's multer allow-list (jpg/jpeg/png/pdf).",
      }),
    uploadSource: z.enum(["FILE_MANAGER", "LOCAL"]).optional().meta({
      description: "For fileUpload fields: whether files come from the CUBE File Manager or a local device upload.",
    }),
    attachmentsData: z
      .array(attachmentsDataSchema)
      .optional()
      .meta({ description: "Predefined attachments bundled with the question (awsKey/name/type each)." }),
    captureLiveLocation: z.boolean().optional().meta({
      description: "For geoLocation fields: if true, capture the device's live GPS location rather than manual entry.",
    }),
    allowManualSelection: z.boolean().optional().meta({
      description:
        "For geoLocation/look-up fields: whether the user may manually pick a value instead of an auto-detected one.",
    }),
    viewType: z
      .enum(["list", "dropDown", "chip", "card"])
      .optional()
      .meta({ description: "Render style for select/look-up fields." }),
    dataToDisplay: z
      .array(z.unknown())
      .optional()
      .meta({ description: "For look-up fields: which attributes of the looked-up entity to display in the answer." }),
    lookUpAnsFieldType: z
      .string()
      .optional()
      .meta({ description: "For look-up fields: the effective field type of the resolved lookup answer." }),
    allowEditingOfPredefinedVal: z
      .boolean()
      .optional()
      .meta({ description: "Whether a user may edit a predefined/preset answer value." }),
    allowPredefinedVal: z
      .boolean()
      .optional()
      .meta({ description: "Whether this field carries a predefined default value." }),
    displayAs: z.enum(["button", "text", "dateAndTime", "dateOnly"]).optional().meta({
      description: "Presentation variant: for date-time (dateAndTime/dateOnly) or url/action fields (button/text).",
    }),
    openIn: z
      .enum(["newTab", "popUp", "sameTab"])
      .optional()
      .meta({ description: "For url fields: where the link opens when clicked." }),
    formulaExpression: safeFormulaExpression.optional().meta({
      description:
        "For formula fields: the arithmetic expression evaluated over mapped field variables. Max 1000 chars; restricted to letters/digits/underscore/whitespace and + - * / % ^ ( ) . , — ranges, matrices, and factorial are rejected server-side.",
    }),
    fieldsVariableMapper: z.record(z.string().length(36), z.string()).optional().meta({
      description:
        "For formula fields: maps each source question's 36-char UUID (key) to the variable name used in formulaExpression (value).",
    }),
    displaySize: z.number().optional().meta({ description: "Layout width/size hint for rendering the field." }),
    seperator: z.string().optional().meta({
      description:
        "For concatenate/autoNumber fields: the string inserted between joined field values (note spelling).",
    }),
    selectedFields: z.array(z.string().length(36)).optional().meta({
      description: "For concatenate fields: ordered list of source question 36-char UUIDs whose values are joined.",
    }),
    numberingMode: z.enum(["standalone", "basedOnFields"]).optional().meta({
      description:
        "For autoNumber fields: standalone assigns a portal/form sequential counter (auto-populated at form creation); basedOnFields derives the number from other fields and is NOT auto-assigned on create.",
    }),
    allowCameraCaptureOnly: z.boolean().optional().meta({
      description: "For image fields: if true, only allow camera capture; disallow file selection from device.",
    }),
  })
  .catchall(z.unknown());

// The member shape only. `formTemplateFieldSchema` below is this plus the cross-field table-section
// rule; split so the rule reads as its own paragraph rather than as a tail on a 40-line literal.
const formTemplateFieldMembers = z.object({
  _id: z.string().meta({
    description:
      "36-char UUID identifying this section within the template (not a Mongo ObjectId); used as the arrayFilter key when cascading edits into live forms. Reuse to edit an existing section, generate a new one to add.",
  }),
  sectionCode: z.string().optional().meta({
    description:
      "Preset-section marker. Absent/empty = a normal user-defined section. Set to a presetSectionsCodeObject value (e.g. SECTION_TYPE_QUANTITY, SECTION_TYPE_MILESTONE, SECTION_TYPE_WBS_ITEMS, SECTION_TYPE_TASK) to make this a special contract section with built-in behaviour.",
  }),
  isDefaultSection: z.boolean().optional().meta({ description: "Marks this as the template's default/first section." }),
  sectionHeading: z.string().meta({ description: "Display title of the section." }),
  questionFields: z
    .array(questionFieldSchema)
    .optional()
    .meta({ description: "Ordered list of question fields in this section." }),
  // SAFE TO TIGHTEN LATER (request-side): the real element is the table ROW already modelled as
  // `tableSectionRow` in forms/inspectionAndProgress.collection.ts — `z.object({ rowId: z.string()
  // .optional(), isPreFilledRow: z.boolean().optional() }).catchall(tableSectionCell)`, where a cell
  // is `{ value?, createdDate?: number, createdBy?: 24-hex }`. cube-qa census over the 18171 stored
  // template sections (Template.template[]): tableDataSource present on 1165 / absent on 17006;
  // outer value ALWAYS an array (array=1165), 1121 of them empty, and all 277 non-empty elements are
  // plain objects (object=277, no scalars/arrays/nulls). Row keys: rowId=269, isPreFilledRow=269,
  // 36-char question UUIDs=1589, _id=8. The form-INSTANCE mirror agrees at larger scale — of 4376
  // sections, 333 carry the field and all 333 are arrays (211 non-empty, 122 empty); all 1184
  // elements are objects, keys rowId=1184, uuid36=8074, isPreFilledRow=992.
  tableDataSource: z
    .array(z.unknown())
    .optional()
    .meta({ description: "For table-type sections: the predefined rows/grid data for the section." }),
  allowNewRowAddInForm: z
    .boolean()
    .optional()
    .meta({ description: "For table sections: whether users may add new rows while filling the form." }),
  allowPreDefineRowValues: z
    .boolean()
    .optional()
    .meta({ description: "For table sections: whether predefined row values are provided." }),
  actualStartDate: z
    .object({
      displayAs: z.enum(["dateAndTime", "dateOnly"]).optional(),
      selectionScope: z
        .enum(["futureDateTime", "pastDateTime", "rangeBased", "anyDateTime", "currentDateTime"])
        .optional(),
    })
    .optional(),
  // Per-section config for the two derived task metrics, carried ONLY by a SECTION_TYPE_TASK section
  // (the TASKS preset renders them as its own columns, not as `questionFields` entries). Optional like
  // the two `actual*Date` configs around them, and for the same reason: cube-qa census over the 18353
  // stored template sections (Template.template[]) finds `progress` on 355 and `remDuration` on 354,
  // all of them on a SECTION_TYPE_TASK section — and even among task sections only 355 / 354 of the
  // 755 carry them. Declaring them required made every OTHER section shape invalid, which is
  // request-reachable: `template: z.array(formTemplateFieldSchema)` is the createTemplate /
  // updateTemplate body, so a template whose sections are ordinary was rejected outright. Not folded
  // into the SECTION_TYPE_TABLE_SECTION rule below either — a task section legitimately omits them
  // (400 do), so no section code makes either key mandatory.
  progress: z
    .object({
      isMandatory: z.boolean(),
    })
    .optional(),
  remDuration: z
    .object({
      isMandatory: z.boolean(),
    })
    .optional(),
  actualEndDate: z
    .object({
      displayAs: z.enum(["dateAndTime", "dateOnly"]).optional(),
      selectionScope: z
        .enum(["futureDateTime", "pastDateTime", "rangeBased", "anyDateTime", "currentDateTime"])
        .optional(),
    })
    .optional(),
  allowEditPreDefineRowInForm: z
    .boolean()
    .optional()
    .meta({ description: "For table sections: whether users may edit predefined rows while filling the form." }),
});

// Table sections are modelled EXACTLY. `questionFields` (the grid's columns) and `tableDataSource`
// (its rows) are `.optional()` above because ordinary sections legitimately omit them — but a
// section whose `sectionCode` says it IS a grid must carry both. Without this the schema admitted
// all four combinations, and each downstream validator then invented its own reaction to the broken
// ones; `isWellFormedTableSection` in helpers/constants.ts records the divergence this closes.
//
// Deliberately one-directional. An EMPTY array satisfies the rule (the builder mints a table section
// as `{ questionFields: [], tableDataSource: [] }`, and a table nobody has added a row to yet keeps
// `tableDataSource: []`), and a non-table section carrying a stray `tableDataSource` is left alone.
// Only an ABSENT key on a self-declared table is rejected, so this cannot fail a request that used to
// succeed for any section the product actually mints. The one shape it does newly reject is a table
// section restored from the section Library that was archived without a `tableDataSource` — which is
// precisely the path that manufactures the malformed document (formTemplate.tsx
// `handleAddSectionsFromLibrary` spreads the library entry verbatim into the template).
//
// `.check()` on a ZodObject returns a ZodObject in Zod 4, so `.extend()` (mailbox.collection.ts) and
// the inferred `FormTemplateField` are both unaffected, and the refinement survives the extend.
export const formTemplateFieldSchema = formTemplateFieldMembers.check((ctx) => {
  if (ctx.value.sectionCode !== SECTION_TYPE_TABLE_SECTION) return;
  (["questionFields", "tableDataSource"] as const).forEach((key) => {
    if (Array.isArray(ctx.value[key])) return;
    ctx.issues.push({
      code: "custom",
      path: [key],
      input: ctx.value,
      message: `A ${SECTION_TYPE_TABLE_SECTION} must carry \`${key}\` as an array (empty is allowed); section "${ctx.value.sectionHeading}" omits it.`,
    });
  });
});

export type QuestionField = z.infer<typeof questionFieldSchema>;
export type FormTemplateField = z.infer<typeof formTemplateFieldSchema>;

const templateTypeSchema = z.object({
  _id: objectIdString.meta({
    description:
      "Forms_type _id (ObjectId 24-hex string) selecting which form-type category this template belongs to; obtain from the formTypesController listing, NOT the type's display name.",
  }),
  name: z
    .string()
    .optional()
    .meta({ description: "Display name of the form type (denormalized copy of the Forms_type name)." }),
  resourceType: z
    .string()
    .optional()
    .meta({ description: "Form category string this type belongs to (e.g. INSPECTION, PROGRESS_UPDATES)." }),
});

const formFlowUserSchema = z.object({
  id: objectIdString.meta({
    description:
      "Account _id (ObjectId 24-hex string) of a user permitted for this flow role; obtain from a space/project users list, not the display name.",
  }),
  name: z
    .string()
    .optional()
    .nullable()
    .meta({ description: "Display name of the user (denormalized for UI; not authoritative)." }),
});

const formFlowSectionSchema = z.object({
  _id: z.string().length(36).meta({
    description: "36-char UUID of a template section this flow level acts on (matches formTemplateFieldSchema._id).",
  }),
  sectionHeading: z.string().meta({ description: "Display heading of the referenced section." }),
  sectionCode: z.string().nullable().optional().meta({
    description: "Preset-section code of the referenced section, if it is a preset section; null/absent otherwise.",
  }),
});

const gridDataSourceSchema = z.object({
  type: z.enum(["Fill", "Modify", "Verify"]).meta({
    description:
      "The action this data-source grants at this level: Fill (answer), Modify (edit), or Verify (approve section).",
  }),
  sections: z
    .array(formFlowSectionSchema)
    .optional()
    .meta({ description: "Template sections this Fill/Modify/Verify permission applies to at this level." }),
  users: z
    .array(formFlowUserSchema)
    .optional()
    .meta({ description: "Users granted the given action on the listed sections at this level." }),
});

const formFlowActionDetailsSchema = z.object({
  userId: objectIdString.optional().meta({
    description:
      "Account id of the user who took the last action at this level; set server-side on submit/approve/reject.",
  }),
  userName: z.string().optional().meta({ description: "Display name of the acting user; set server-side." }),
  actionName: z
    .string()
    .optional()
    .meta({ description: "The action taken (e.g. SUBMITTED/APPROVED/REJECTED); set server-side." }),
  actionMessage: z
    .string()
    .optional()
    .meta({ description: "Message/comment attached to the action; set server-side." }),
  message: z
    .string()
    .optional()
    .meta({ description: "Alternate/legacy comment field for the action; set server-side." }),
  timeStamp: z.number().optional().meta({ description: "Epoch ms when the action occurred; set server-side." }),
  signDocAwsKey: z
    .string()
    .optional()
    .nullable()
    .meta({ description: "S3 key of the signature document attached to the action, if any; set server-side." }),
  // Both are written by forms.service.ts (:4109, :4586, :4725) and are on 389 / 953 stored levels,
  // but were undeclared here — so the web `FormFlowActionDetails` type could not reach them (the
  // sidebar could only render `signDocAwsKey`), and a request echoing them back had them stripped
  // by `req.body = validatedData`. Declaring them only widens what this schema accepts.
  signDocType: z
    .string()
    .optional()
    .nullable()
    .meta({ description: "Signature type used for the attached signature document; set server-side." }),
  profilePic: z
    .string()
    .optional()
    .nullable()
    .meta({ description: "Profile picture key of the acting user, snapshotted at action time; set server-side." }),
});

const formFlowLevelSchema = z.object({
  dataSource: z.array(gridDataSourceSchema).meta({
    description:
      "The Fill/Modify/Verify permission grids (section+user) that define who can act on which sections at this approval level.",
  }),
  heading: z.string().min(1).max(100).meta({ description: "Display name of this approval level/step." }),
  submitBtnTxt: z
    .string()
    .max(50)
    .optional()
    .meta({ description: "Custom label for the Submit button at this level." }),
  allowDynamicUserConfig: z.boolean().optional().meta({
    description:
      "If true, the allowed users at this level may be reassigned at fill-time rather than fixed by the template.",
  }),
  rejectBtnTxt: z
    .string()
    .max(50)
    .optional()
    .meta({ description: "Custom label for the Reject button at this level." }),
  approveBtnTxt: z
    .string()
    .max(50)
    .optional()
    .meta({ description: "Custom label for the Approve button at this level." }),
  rollBackBtnTxt: z
    .string()
    .max(50)
    .optional()
    .meta({ description: "Custom label for the Roll-back button at this level." }),
  allowedUsersToSubmit: z.array(formFlowUserSchema).optional().meta({
    description: "Users permitted to submit this level; replaced by usersData when instantiating from library.",
  }),
  allowedUsersToReject: z
    .array(formFlowUserSchema)
    .optional()
    .meta({ description: "Users permitted to reject at this level." }),
  allowedUsersToRollBack: z
    .array(formFlowUserSchema)
    .optional()
    .meta({ description: "Users permitted to roll back at this level." }),
  allowedUsersToApprove: z
    .array(formFlowUserSchema)
    .optional()
    .meta({ description: "Users permitted to approve at this level." }),
  actionStatus: z.boolean().optional().meta({
    description:
      "Runtime state of this level's action on a live form instance; managed server-side, not set at template-build time.",
  }),
  actionDetails: formFlowActionDetailsSchema
    .optional()
    .meta({ description: "Runtime record of the action taken at this level on a live form; populated server-side." }),
});

// Form Flow building-block types (derived from the zod schemas above).
export type FormFlowUser = z.infer<typeof formFlowUserSchema>;
export type FormFlowSection = z.infer<typeof formFlowSectionSchema>;
export type formFlowDataSource = z.infer<typeof gridDataSourceSchema>;
export type FormFlowActionDetails = z.infer<typeof formFlowActionDetailsSchema>;
export type FormFlowStep = z.infer<typeof formFlowLevelSchema>;

const selectedFormSchema = z.object({
  _id: objectIdString.meta({
    description:
      "Forms template _id (ObjectId 24-hex string) this flow is built on; get from the formController listing. Marked formIsInUse=true when the flow is saved.",
  }),
  name: z.string().optional().meta({ description: "Denormalized template name." }),
  description: z.string().optional().meta({ description: "Denormalized template description." }),
  type: z
    .object({
      _id: objectIdString.meta({ description: "Forms_type _id of the template's type category." }),
      name: z.string().optional().meta({ description: "Denormalized form-type name." }),
      description: z.string().optional().meta({ description: "Denormalized form-type description." }),
    })
    .optional()
    .meta({ description: "The template's form-type category (denormalized)." }),
  template: z
    .array(formTemplateFieldSchema)
    .meta({ description: "The full section/question structure copied from the Forms template." }),
});

const linkedFormSchema = z.object({
  _id: objectIdString.meta({
    description: "InspectionAndProgress form-instance _id (ObjectId 24-hex string) being linked into this form.",
  }),
  // Optional, matching `inspectionAndProgress.collection.ts` and the stored data. It was required
  // here, which contradicted both: the collection type documents that legacy entries carry
  // `formTitle`/`formDescription` instead, and staging (`cube-qa`) confirms it — of 98 stored
  // `linkedForms` entries across 26 documents, only 73 have `resourceType` and 25 are the legacy
  // shape. Requiring it meant any update that echoes an existing linkedForms array back (linking or
  // unlinking on one of those documents) was rejected by request validation before it reached the
  // service.
  resourceType: z
    .string()
    .optional()
    .meta({ description: "Form category of the linked form instance (e.g. INSPECTION, PROGRESS_UPDATES)." }),
});

export const addSectionToLibraryRequest = z
  .object({
    formId: objectIdString.meta({
      description:
        "Source Forms template _id (ObjectId 24-hex string) the section is copied from; obtain from the formController listing. Stored as data.formId on the Library entry.",
    }),
    sectionId: z.string().length(36).meta({
      description:
        "36-char UUID of the section (matches formTemplateFieldSchema._id). This is the UPSERT KEY: an existing Library section with the same sectionId in this portal is overwritten. Not a Mongo ObjectId.",
    }),
    sectionHeading: zPlainTextRequired.meta({ description: "Display heading stored for the library section." }),
    resourceType: z.string().meta({
      description:
        "Form category the section belongs to (e.g. INSPECTION). Stored on the Library entry but NOT used as part of the upsert match key (only sectionId + portalId are matched).",
    }),
    questionFields: z
      .array(questionFieldSchema)
      .optional()
      .meta({ description: "The question fields captured into the reusable library section." }),
    sectionCode: z
      .string()
      .optional()
      .meta({ description: "Preset-section code, if the section is a preset section." }),
    allowNewRowAddInForm: z
      .boolean()
      .optional()
      .meta({ description: "Table-section flag: allow adding new rows at fill time." }),
    allowPreDefineRowValues: z
      .boolean()
      .optional()
      .meta({ description: "Table-section flag: predefined row values are provided." }),
    // SAFE TO TIGHTEN LATER (request-side): same row shape as formTemplateFieldSchema.tableDataSource
    // above (`tableSectionRow` in forms/inspectionAndProgress.collection.ts). This body is persisted
    // verbatim as `Library.data`, so the census is on the Library side: of the 64 Library entries
    // (TEMPLATE_SECTION=56, FORM_FLOW=8) 3 carry a tableDataSource, all 3 arrays, both non-empty
    // elements plain objects (object=2), keys rowId=2, isPreFilledRow=2, 36-char question UUIDs=8.
    tableDataSource: z.array(z.unknown()).optional().meta({ description: "Table-section predefined rows/grid data." }),
  })
  .meta({
    description:
      "Saves (upserts) a reusable form-template Section into the portal's Library collection as a TEMPLATE_SECTION entry, matched by sectionId within the caller's portalId so one tenant cannot overwrite another's. Pass the source formId (a Forms template _id) plus the section's fields (sectionId is a 36-char UUID, sectionHeading, questionFields, etc.); portalId and the author are taken from the session, not the body. No dedicated permission check runs in the service.",
  });

export const addSectionToLibraryResponse = formStatusResponse.meta({
  description: "Response for add section to library",
});

export const uploadFilesInTemplateResponse = extendStatusResponse({
  // S3 key (uuid) of the first uploaded file — convenience for single-file uploads
  uuid: z.string().optional(),
  uploadedFiles: z
    .array(
      z.object({
        uuid: z.string(),
        fileName: z.string(),
        mimeType: z.string(),
        size: z.number().nonnegative(),
      }),
    )
    .optional(),
}).meta({
  description: "Response for uploading files in a form template (returns S3 uuid keys)",
});

export const getContractFormBoqItemsRequest = z
  .object({
    contractId: objectIdString.meta({
      description: "Contract _id (ObjectId 24-hex string); obtain from the contracts listing, not the contract name.",
    }),
    contractVersionId: objectIdString.meta({
      description: "The specific Contract version _id (ObjectId 24-hex string) to read forms/BOQ items from.",
    }),
    resourceType: z.enum([formCategories.inspection, formCategories.progressUpdates, formCategories.bills]).meta({
      description:
        "Form category to aggregate BOQ items for; must be one of the inspection, progressUpdates, or bills category constants.",
    }),
  })
  .meta({
    description:
      "Read-only: for a Contract version, lists all non-deleted forms of the given category the caller can view and, for each, returns formId/formName/formNo/status/archived flag, the form template, and its attached BOQ items (fetched per-form via getFormBoqItems). Requires RESOURCE_VIEW permission on the contract. Inputs: contractId and contractVersionId (a Contract and one of its version ids), plus resourceType which must be one of the inspection, progressUpdates, or bills form categories. GET route; query params are converted to the body. Differs from getFormBoqItems (single form) by aggregating BOQ items across all forms on a contract version.",
  });

export const getContractFormBoqItemsResponse = formStatusResponse.meta({
  description: "Response for get contract form boq items",
});

const fetchFormEntriesFilterableFields = [
  "counter",
  "title",
  "description",
  "lastAction.actionName",
  "isArchived.status",
  "status",
  "lastAction.timeStamp",
  "lastAction.userName",
] as const;
const fetchFormEntriesWhere = filterWhereClauseFor(fetchFormEntriesFilterableFields, {
  patterns: dynamicCustomFieldPatterns,
});

export const fetchFormEntriesRequest = z
  .object({
    value: z.object({
      formId: objectIdString.meta({
        description:
          "Forms TEMPLATE _id (ObjectId 24-hex string) — the selectedForm._id whose instances to list; get from the formController listing. NOT a form-instance id.",
      }),
      projectId: objectIdString.meta({
        description:
          "Project _id (ObjectId 24-hex string) scoping the returned form instances; from the projects listing.",
      }),
      getExtractedEntries: z.boolean().optional().meta({
        description:
          "If true, returns the flattened/extracted answer-entries grid instead of the standard instance grid rows.",
      }),
      requiresCounts: z
        .boolean()
        .optional()
        .meta({ description: "If true, also returns the total matching-instance count for pagination." }),
      skip: z.number().int().nonnegative().optional().meta({ description: "Pagination offset (rows to skip)." }),
      take: z
        .number()
        .int()
        .nonnegative()
        .max(1000000)
        .optional()
        .meta({ description: "Pagination page size (max rows to return)." }),
      where: fetchFormEntriesWhere.meta({
        description:
          "Grid filter clause restricted to the allowed columns (counter, title, description, status, lastAction.*, isArchived.status) plus dynamic custom-field/question columns.",
      }),
      // Sortable columns == the filterable allow-list (all base grid columns carry a filterType and
      // are sortable; formFlow.heading is disableSorting). formsPatch=true lets the query builder sort
      // on a form-question column keyed by its UUID — allowed via the form-question pattern in
      // dynamicCustomFieldPatterns.
      sorted: sortConfigFor(fetchFormEntriesFilterableFields, { patterns: dynamicCustomFieldPatterns }),
    }),
  })
  .meta({
    description:
      "Lists the form instances (InspectionAndProgress documents) created from a given Form template within a Project, with grid where/sorted/skip/take/requiresCounts and an optional getExtractedEntries mode; returns per-instance grid data (title, status, currentLevelOfForm, answers). value.formId is the Forms template _id (selectedForm._id, e.g. from a Forms list) and value.projectId is the Project _id. Only returns instances where the session user is in users[] with RESOURCE_VIEW, and only latest-version instances for contract forms.",
  });

export const fetchFormEntriesResponse = formStatusResponse.meta({
  description: "Response for fetch form entries",
});

// Service uses: entity, sorted, where, skip, take, requiresCounts
// Controller spreads: req.body.value
export const getEntitesFromLibraryRequest = z
  .object({
    value: z
      .object({
        entity: z.enum(["TEMPLATE_SECTION", "FORM_FLOW"]).optional().meta({
          description:
            "The Library entry type to list — matched exactly against Library.entity. Use TEMPLATE_SECTION for saved sections or FORM_FLOW for saved flows. This is the ONLY content filter applied (besides portal scope).",
        }),
        resourceType: z.string().optional().meta({
          description:
            "IGNORED by the service: getEntitesFromLibrary matches only on entity + portalId and never reads resourceType. Passing it has no effect.",
        }),
        skip: z.number().nonnegative().optional().meta({ description: "Pagination offset (entries to skip)." }),
        take: z.number().nonnegative().optional().meta({ description: "Pagination page size (max entries)." }),
        sorted: sortConfigFor(libraryEntitiesFilterableFields, { patterns: dynamicCustomFieldPatterns }).meta({
          description:
            "Grid sort configuration; defaults to createdDate descending. Restricted to the library grid's sortable columns (name, selectedForm, space.spaceName, createdBy, createdDate).",
        }),
        where: libraryEntitiesWhere.meta({
          description:
            "Optional grid filter clause over allowed library columns (name, createdBy, createdDate, _id, space.spaceName, data.resourceType, data.formCategory) plus dynamic custom-field patterns.",
        }),
        requiresCounts: z
          .boolean()
          .optional()
          .meta({ description: "If true, also returns the total matching-entry count." }),
      })
      .optional(),
  })
  .meta({
    description:
      "Lists reusable Library entries for the current portal, filtered by the value.entity type (e.g. TEMPLATE_SECTION or FORM_FLOW) with optional grid where/sorted/skip/take and requiresCounts; returns each entry's flattened data with _id, createdBy and createdDate. Read-only, scoped to the session portalId. Use this to browse saved sections/form-flows before reusing one.",
  });

export const getEntitesFromLibraryResponse = formStatusResponse.meta({
  description: "Response for get entities from library",
});

export const deleteSectionsFromLibraryRequest = z
  .object({
    // Library document _ids fed to new ObjectId(...).
    sectionIds: z.array(objectIdString).min(1).meta({
      description:
        "Library entry _ids (ObjectId 24-hex strings) to hard-delete, as returned by getEntitesFromLibrary (the entry _id). NOT the 36-char sectionId UUID used when adding.",
    }),
    resourceType: z.string().meta({
      description:
        "IGNORED by the service: deletion is scoped only by _id and portalId; resourceType is not part of the delete filter.",
    }),
  })
  .meta({
    description:
      "Permanently removes (hard deleteMany) Library entries by their document _ids, scoped to the session portalId. sectionIds must be the Library entry _ids returned by getEntitesFromLibrary (NOT the 36-char sectionId UUID used when adding). No permission check beyond portal scoping.",
  });

export const deleteSectionsFromLibraryResponse = formStatusResponse.meta({
  description: "Response for delete sections from library",
});

export const insertTemplateTypeSchema = z
  .object({
    action: z.string().meta({
      description:
        'Send "insert" (retained for request compatibility). The /insertTemplateType endpoint is a dedicated insert (formsService.insertTemplateType) — no dispatcher branch.',
    }),
    data: z.object({
      name: zPlainTextRequired.meta({
        description: "Name of the new form-type category; rejected if a duplicate name exists in the portal.",
      }),
      resourceType: z
        .string()
        .meta({ description: "Form category this type belongs to (e.g. INSPECTION, PROGRESS_UPDATES)." }),
      description: zSafeHtml
        .refine((val) => val.trim().length > 0, { message: "This field is required" })
        .meta({ description: "Sanitized HTML description of the form type." }),
    }),
  })
  .meta({
    description:
      "Write: creates a new Form type category (Forms_types) in the current portal from data.name, data.resourceType and data.description. Enforces the CREATE_FORM_TEMPLATES portal permission (returns a no-permission error otherwise). Dedicated insert endpoint (formsService.insertTemplateType). Use updateTemplateType (which carries _id and requires EDIT_FORM_TEMPLATES) to edit an existing type.",
  });

export const updateTemplateTypeSchema = z
  .object({
    action: z.string().meta({
      description:
        'Send "update" (retained for request compatibility). The /updateTemplateType endpoint always updates by _id.',
    }),
    _id: objectIdString.meta({
      description:
        "Forms_type _id (ObjectId 24-hex string) to update; obtain from the formTypesController listing, not the type name.",
    }),
    data: z.object({
      name: zPlainTextRequired.meta({
        description: "New name; cascaded into embedded type.name on all matching Forms templates and form instances.",
      }),
      description: zSafeHtml.meta({
        description: "New sanitized HTML description; cascaded into embedded type.description across the portal.",
      }),
    }),
  })
  .meta({
    description:
      "Write: updates an existing Form type category (Forms_types) identified by _id, setting data.name and data.description, and cascades the new name/description into embedded type fields on all matching Forms templates and InspectionAndProgress instances in the portal. Enforces the EDIT_FORM_TEMPLATES portal permission. Inputs: _id (a Forms_type id, e.g. from formTypesController list) plus data.name/description. A dedicated update endpoint (formsService.updateTemplateType); insertTemplateType handles creation.",
  });

export const deleteTemplateTypeSchema = z
  .object({
    action: z.string().meta({ description: 'Dispatcher action selector; send "delete".' }),
    inspectionFormTypeIds: z.array(objectIdString).min(1).meta({
      description:
        "Forms_type _ids (ObjectId 24-hex strings) to soft-delete; from the formTypesController listing. Delete is blocked (nothing removed) if any non-deleted Forms template references any listed type.",
    }),
  })
  .meta({
    description:
      'Soft-deletes (sets isDeleted:true) one or more Form Type category records in the current portal, given inspectionFormTypeIds. Guarded by usage: if any non-deleted Form template references a listed type (Forms."type._id"), nothing is deleted and an errorMessage naming those Forms is returned. Get the type ids from the /formTypesController listing (templateTypesDefaultSchema). Portal scoping is automatic; no explicit permission check in this handler.',
  });

// Controller spreads req.body.value to service, so resourceType must be inside value
export const templateTypesDefaultSchema = z
  .object({
    action: z.string().optional().meta({
      description: "Dispatcher action selector; omit or send a non-mutating value for the list/default branch.",
    }),
    value: gridValueSchema
      .extend({
        // Sortable columns match the form-types grid (name, description, createdDate, createdBy) — the
        // same set as the filterable allow-list — so reuse it. Overrides the open base `sorted`.
        sorted: sortConfigFor(templateTypesListFilterableFields, { patterns: dynamicCustomFieldPatterns }),
        where: templateTypesListWhere.meta({
          description:
            "Grid filter clause over allowed columns (name, description, createdDate, createdBy) plus dynamic custom-field patterns.",
        }),
        resourceType: z.string().optional().meta({
          description: "Form category to filter types by (e.g. INSPECTION); goes inside value, not at the top level.",
        }),
      })
      .optional()
      .meta({
        description: "Grid paging/filter envelope: skip, take, sorted, where, requiresCounts plus resourceType.",
      }),
  })
  .meta({
    description:
      "Read-only: lists Form type categories (Forms_types collection) for the current portal filtered by resourceType, with grid-style where/sorted/skip/take and optional total count (requiresCounts). Portal-scoped automatically; resourceType goes inside value. Returns the form-type documents used to categorise Form templates. This is the list/default action of the form-types controller; use insertTemplateType/updateTemplateType/deleteTemplateType to mutate.",
  });

export function getTemplateTypesRequestSchema(action?: string) {
  switch (action) {
    case "insert":
      return insertTemplateTypeSchema;
    case "update":
      return updateTemplateTypeSchema;
    case "delete":
      return deleteTemplateTypeSchema;
    default:
      return templateTypesDefaultSchema;
  }
}

export const getTemplateTypesResponse = formStatusResponse.meta({
  description: "Response for form types controller",
});

// Discriminated union composed from the SAME per-action schemas the dynamicSchema
// dispatcher (getTemplateTypesRequestSchema) returns — single source of truth. The
// default (no/unknown action) variant is selected via a preprocess that maps any value
// outside the explicit action set to the "__default__" sentinel.

// The three per-question flag rules for mailbox/issues templates (forms/CLAUDE.md §7.5). The flags are
// declared on `questionFieldSchema` itself, so every predicate below reads an exactly-typed field —
// `QuestionFieldFlags` is a Pick off the inferred question type, not a hand-rolled restatement of it.
export const fieldToggleErrorMessage = "'Editable in forms' and 'Is Metadata' cannot be toggled off at the same time.";

/** The three flags the rules below are expressed over, taken straight off the inferred question type. */
export type QuestionFieldFlags = Pick<QuestionField, "editableInForm" | "isMetadata" | "isMandatory">;

/** Only mailbox/issues templates carry the two flags, so only they are subject to the rules. */
export const usesFieldFlagToggles = (resourceType: string | undefined): boolean =>
  resourceType === formCategories.mailBox || resourceType === formCategories.issues;

export const mandatoryErrorMessage = "'Mandatory' cannot be enabled while 'Editable in forms' is off.";

export const fieldFlagsMissingErrorMessage =
  "Every question of a mailbox or issues form template must carry both the 'Editable in forms' and 'Is Metadata' flags.";

/**
 * Both flags are REQUIRED on these two resource types. They stay `.optional()` on the schema because
 * the answer-save routes replay stored pre-toggle questions through it, but a template being authored
 * must state them explicitly: every producer does (`getNewInputField` for a new field, the library
 * import for a borrowed one) and the boot backfill stamps them onto every stored mailbox/issues
 * template, so an absent flag here is a producer bug rather than the documented `?? true` default.
 */
export const fieldFlagsPresent = (field: QuestionFieldFlags | null | undefined): boolean =>
  typeof field?.editableInForm === "boolean" && typeof field?.isMetadata === "boolean";

/** A single question field is valid unless BOTH flags are explicitly off. */
export const fieldToggleRuleSatisfied = (field: QuestionFieldFlags | null | undefined): boolean =>
  !(field?.editableInForm === false && field?.isMetadata === false);

/** A field the form never renders can't be mandatory in it. */
export const mandatoryRuleSatisfied = (field: QuestionFieldFlags | null | undefined): boolean =>
  !(field?.isMandatory === true && field?.editableInForm === false);

/**
 * The behavioural flag rule a single question field breaks, or null when it satisfies both.
 *
 * Deliberately does NOT include `fieldFlagsPresent`: this is the per-question judgement used by the
 * incremental `updateUsedTemplate` route, where a question's flags are reconstructed by replaying
 * positional edits onto the STORED question. Requiring presence there would reject an unrelated toggle
 * on any stored question the boot backfill did not reach, which is precisely the "template becomes
 * unsaveable with no usable error" failure the presence rule is meant to prevent. Presence is enforced
 * only where a COMPLETE template is authored — `flagsPresentRuleSatisfiedInTemplate`.
 */
export const fieldFlagRuleError = (field: QuestionFieldFlags | null | undefined): string | null => {
  if (!fieldToggleRuleSatisfied(field)) return fieldToggleErrorMessage;
  if (!mandatoryRuleSatisfied(field)) return mandatoryErrorMessage;
  return null;
};

// Nullable/optional throughout: `template` reaches these helpers both from a parsed request and
// straight out of Mongo, where a malformed section or question row must not throw.
const everyField = (
  template: FormTemplateField[] | null | undefined,
  predicate: (field: QuestionFieldFlags) => boolean,
): boolean => (template ?? []).every((section) => (section?.questionFields ?? []).every(predicate));

export const flagsPresentRuleSatisfiedInTemplate = (
  resourceType: string | undefined,
  template: FormTemplateField[] | null | undefined,
): boolean => !usesFieldFlagToggles(resourceType) || everyField(template, fieldFlagsPresent);

export const onlyOneToggleRuleSatisfied = (
  resourceType: string | undefined,
  template: FormTemplateField[] | null | undefined,
): boolean => !usesFieldFlagToggles(resourceType) || everyField(template, fieldToggleRuleSatisfied);

export const mandatoryRuleSatisfiedInTemplate = (
  resourceType: string | undefined,
  template: FormTemplateField[] | null | undefined,
): boolean => !usesFieldFlagToggles(resourceType) || everyField(template, mandatoryRuleSatisfied);

export const saveTemplateSchema = z
  .object({
    action: z.string().meta({
      description:
        'Send "insert" (retained for request compatibility). The /saveTemplate endpoint is a dedicated insert (formsService.saveTemplate).',
    }),
    parentFormId: objectIdString.optional().meta({
      description:
        "If this template is being duplicated from another, the source Forms template _id (ObjectId 24-hex string); its noOfDuplicatesOfthisForm counter is incremented. Omit for a brand-new template.",
    }),
    data: z.object({
      name: zPlainTextRequired.meta({ description: "Name of the new form template." }),
      resourceType: z
        .string()
        .meta({ description: "Form category the template belongs to (e.g. INSPECTION, PROGRESS_UPDATES)." }),
      type: templateTypeSchema.meta({
        description:
          "The form-type category object; type._id is a Forms_type _id from the formTypesController listing. Marked isUsedInForms=true on insert.",
      }),
      description: zSafeHtml
        .refine((val) => val.trim().length > 0, { message: "This field is required" })
        .meta({ description: "Sanitized HTML description of the template." }),
      template: z
        .array(formTemplateFieldSchema)
        .meta({ description: "The section/question structure of the template." }),
      lookUpUsed: z
        .array(z.enum(["Location", "Teams"]))
        .optional()
        .meta({
          description: "Which look-up entity sets (Location/Teams) any look-up questions in the template reference.",
        }),
    }),
  })
  .refine(
    (body) =>
      body.data.resourceType === formCategories.mailBox ||
      !(body.data.template ?? []).some((section) => section.isDefaultSection !== undefined),
    {
      error: `isDefaultSection is only allowed on ${formCategories.mailBox} form templates.`,
      path: ["data", "template"],
    },
  )
  .refine((body) => flagsPresentRuleSatisfiedInTemplate(body.data.resourceType, body.data.template), {
    error: fieldFlagsMissingErrorMessage,
    path: ["data", "template"],
  })
  .refine((body) => onlyOneToggleRuleSatisfied(body.data.resourceType, body.data.template), {
    error: fieldToggleErrorMessage,
    path: ["data", "template"],
  })
  .refine((body) => mandatoryRuleSatisfiedInTemplate(body.data.resourceType, body.data.template), {
    error: mandatoryErrorMessage,
    path: ["data", "template"],
  })
  .meta({
    description:
      "Creates a new Form template in the Forms collection (dedicated insert endpoint, no _id). Requires the CREATE_FORM_TEMPLATES portal permission. Takes data{name, resourceType, type, description, template[], lookUpUsed?}; type is a {_id,name,...} object whose _id comes from the /formTypesController listing. If parentFormId is given, increments that template's noOfDuplicatesOfthisForm. Marks the chosen Form Type as isUsedInForms:true. Use editTemplateSchema (with _id) to edit an existing template. `isDefaultSection` may only appear on a section of a MAILBOX template; sending it on any other resourceType is rejected. MAILBOX and ISSUES templates additionally carry per-question visibility flags and are rejected unless every question satisfies all three: (1) both editableInForm and isMetadata are PRESENT as booleans — they are required on these two resource types, and every producer states them explicitly; (2) editableInForm and isMetadata are not BOTH false — the question would be unreachable from the create form and the detail sidebar alike; (3) isMandatory is not true while editableInForm is false — a question the form never renders cannot be required in it. No rule applies to any other resourceType, where both flags are ignored. (Both flags stay optional on the field schema itself because the answer-save routes replay stored pre-toggle questions through it, and a missing flag is read as true there.)",
  });

export const editTemplateSchema = z
  .object({
    action: z.string().meta({
      description:
        'Send "update" (retained for request compatibility). The /editTemplate endpoint always updates by _id.',
    }),
    _id: objectIdString.meta({
      description: "Forms template _id (ObjectId 24-hex string) to update; obtain from the formController listing.",
    }),
    updateName: z.boolean().optional().meta({
      description:
        "If true, allows editing even when the template is already in use (formIsInUse). Without it, editing an in-use template's content is blocked — use updateUsedTemplate for structural edits to in-use templates.",
    }),
    data: z.object({
      name: zPlainTextRequired.meta({
        description: "New template name; cascaded into embedded selectedForm.name across live instances and flows.",
      }),
      type: templateTypeSchema.meta({
        description: "New form-type object; type._id from the formTypesController listing.",
      }),
      description: zSafeHtml.meta({ description: "New sanitized HTML description; cascaded to live instances/flows." }),
      template: z.array(formTemplateFieldSchema).optional().meta({
        description:
          "New section/question structure; content edits blocked on in-use templates unless updateName is set.",
      }),
      lookUpUsed: z
        .array(z.enum(["Location", "Teams"]))
        .optional()
        .meta({
          description: "Which look-up entity sets (Location/Teams) the template's look-up questions reference.",
        }),
    }),
  })
  .meta({
    description:
      "Updates an existing Form template by _id (dedicated update endpoint). Requires the EDIT_FORM_TEMPLATES portal permission. Editing template content is blocked if the template is already in use (formIsInUse:true) unless updateName:true. On success it cascades the new name/description/type into every live InspectionAndProgress instance and formFlow that embeds this template. Get _id from the /formController listing (templateDefaultSchema) and data.type._id from /formTypesController. For structural add/edit/delete of questions on an in-use template use updateUsedTemplateSchema instead. When data.template is sent for a MAILBOX or ISSUES template it must satisfy the same three per-question flag rules as saveTemplateSchema (both flags present as booleans; editableInForm/isMetadata not both false; isMandatory not true while editableInForm is false), else the call returns status:false with that rule's errorMessage. The resourceType is read from the STORED document, not from the request, so it cannot be side-stepped by sending a different one.",
  });

export const deleteTemplateSchema = z
  .object({
    action: z.string().meta({ description: 'Dispatcher action selector; send "delete".' }),
    InspectionFormIds: z.array(objectIdString).meta({
      description:
        "Forms TEMPLATE _ids (ObjectId 24-hex strings) to soft-delete; from the formController listing. Blocked if any listed template is in use (referenced by a form instance or a project's formTemplates). Note the misleading field name — these are template ids, not instance ids.",
    }),
  })
  .meta({
    description:
      "Soft-deletes (isDeleted:true) one or more Form templates in the portal, given InspectionFormIds. Requires the DELETE_FORM_TEMPLATES portal permission. Blocked if any listed template is in use — referenced by an InspectionAndProgress instance or a project's formTemplates — returning an errorMessage listing the template names. On success, also flips each affected Form Type's isUsedInForms to false when no live Forms remain. Get template ids from /formController (templateDefaultSchema).",
  });

export const archiveTemplateSchema = z
  .object({
    action: z.string().meta({ description: 'Dispatcher action selector; send "archive".' }),
    isArchived: z
      .boolean()
      .meta({ description: "Target archive state written to Forms.isArchived: true to archive, false to unarchive." }),
    formIds: z.array(objectIdString).meta({
      description: "Forms TEMPLATE _ids (ObjectId 24-hex strings) to (un)archive; from the formController listing.",
    }),
  })
  .meta({
    description:
      "Archives or unarchives Form templates by setting Forms.isArchived to the boolean isArchived flag for the given formIds in the portal. Requires the EDIT_FORM_TEMPLATES portal permission. This toggles a template's archive state only; it does not delete (see deleteTemplateSchema) and does not touch form instances. Get formIds from /formController (templateDefaultSchema).",
  });

// --- updateUsedTemplate `changes` (was z.any()) ---
// `changes` is an object keyed by templateEditCases; each value is an ARRAY of per-edit objects the
// service iterates. Element schemas are looseObject so the builder's dynamic/extra keys survive
// (never stripped): CONFIGURE_SECTION carries a computed key equal to `fieldType`, CONFIGURE_QUESTION
// a free `configuration` map, and the builder sends unread `sectionIndex`/`fromIndex` helpers.
// `newQuestion` reuses questionFieldSchema (which has `.catchall`) so arbitrary per-fieldType config
// survives. Every field optional — the schema must accept exactly what the (single) web builder sends
// and never tighten it, since validate-request enforces this on the real HTTP call.

const templateAddQuestionChange = z.looseObject({
  sectionId: z.string().length(36).meta({ description: "Target section _id (36-char UUID) to add the question into." }),
  questionIndex: z
    .number()
    .int()
    .nonnegative()
    .optional()
    .meta({ description: "Insert position ($position); sent only by the duplicate path." }),
  newQuestion: questionFieldSchema.optional().meta({ description: "The full question-field object to insert." }),
});
const templateEditQuestionChange = z.object({
  sectionId: z.string().length(36),
  questionId: z.string().length(36).meta({ description: "Question _id (36-char UUID) to edit." }),
  questionValue: z.string().optional().meta({ description: "New questionValue to $set." }),
});
const templateDeleteQuestionChange = z.object({
  sectionId: z.string().length(36),
  questionId: z.string().length(36).meta({ description: "Question _id (36-char UUID) to delete." }),
});
const templateConfigureQuestionChange = z.object({
  sectionId: z.string().length(36),
  questionId: z.string().length(36),
  // `looseObject`, not `record(string, unknown)`: every key here is `$set` RAW onto the stored question by
  // the CONFIGURE_QUESTION branch, which persists `data.configuration[key]` verbatim and cascades it into
  // every live form instance. An untyped map therefore lets a non-boolean flag reach Mongo. The three
  // MAILBOX/ISSUES flags are declared as booleans so `{ editableInForm: "false" }` is rejected at the
  // route boundary — /updateUsedTemplate is the only caller, so this schema is the only place that can
  // stop it. `fieldFlagErrorAfterChanges` cannot: `fieldFlagRuleError` deliberately omits
  // `fieldFlagsPresent`, and the two predicates it does run compare `=== false` / `=== true`, which a
  // string satisfies vacuously. Storing the string would break both directions at once — every
  // answer-save route replays the stored question through `questionFieldSchema`, where these are
  // `z.boolean()`, while the `?? true` / `!== false` readers would see a TRUTHY `"false"` and treat the
  // flag as on. The catchall keeps every other question property open, which is what the builder sends.
  configuration: z
    .looseObject({
      editableInForm: z.boolean().optional(),
      isMetadata: z.boolean().optional(),
      isMandatory: z.boolean().optional(),
    })
    .meta({
      description:
        "Open map of question-field property name → new value; each key is $set on the question. The MAILBOX/ISSUES flags editableInForm, isMetadata and isMandatory must be BOOLEANS when present: they are written straight through to the stored question and to every cascaded form instance, and a non-boolean makes the stored template unparseable by the answer-save routes while reading as truthy (i.e. flag on) everywhere else.",
    }),
});
const templateConfigureSectionChange = z.looseObject({
  sectionId: z.string().length(36),
  fieldType: z.string().meta({
    description: "Names the section field to set; the new value is carried under a dynamic key equal to this string.",
  }),
});

const templateEditSectionHeadingChange = z.object({
  sectionId: z.string().length(36),
  sectionHeading: z.string().meta({ description: "New section heading to $set." }),
});

const templateDeleteSectionChange = z.object({
  sectionId: z.string().length(36).meta({ description: "Section _id (36-char UUID) to delete." }),
});

const templateChangeSectionSequenceChange = z.object({
  sectionId: z.string().length(36),
  toIndex: z.number().meta({ description: "New position ($position) for the section." }),
  fromIndex: z.number().meta({ description: "Builder helper; not read by the service." }),
});

const templateChangeQuestionSequenceChange = z.object({
  questionId: z.string().length(36),
  sectionId: z.string().length(36),
  toIndex: z.number().meta({ description: "New position ($position) for the question." }),
  fromIndex: z.number().meta({ description: "Builder helper; not read by the service." }),
});
export const templateChangesSchema = z
  .object({
    [templateEditCases.ADD_QUESTION]: z.array(templateAddQuestionChange).optional(),
    [templateEditCases.EDIT_QUESTION]: z.array(templateEditQuestionChange).optional(),
    [templateEditCases.DELETE_QUESTION]: z.array(templateDeleteQuestionChange).optional(),
    [templateEditCases.CONFIGURE_QUESTION]: z.array(templateConfigureQuestionChange).optional(),
    [templateEditCases.CONFIGURE_SECTION]: z.array(templateConfigureSectionChange).optional(),
    [templateEditCases.EDIT_SECTION_HEADING]: z.array(templateEditSectionHeadingChange).optional(),
    [templateEditCases.DELETE_SECTION]: z.array(templateDeleteSectionChange).optional(),
    [templateEditCases.CHANGE_SECTION_SEQUENCE]: z.array(templateChangeSectionSequenceChange).optional(),
    [templateEditCases.CHANGE_QUESTION_SEQUENCE]: z.array(templateChangeQuestionSequenceChange).optional(),
  })
  .meta({
    description:
      "Structural edits keyed by templateEditCases; each key maps to an array of per-edit objects. Send only the case keys you are editing (empty arrays are fine).Extra keys are preserved.",
  });

export const updateUsedTemplateSchema = z
  .object({
    action: z.string().meta({ description: 'Dispatcher action selector; send "updateUsedTemplate".' }),
    changes: templateChangesSchema.optional().meta({
      description:
        "Object keyed by templateEditCases (ADD_QUESTION, EDIT_QUESTION, DELETE_QUESTION, CONFIGURE_QUESTION, EDIT_SECTION_HEADING, CONFIGURE_SECTION, DELETE_SECTION, CHANGE_SECTION_SEQUENCE, CHANGE_QUESTION_SEQUENCE). Each key is an array of per-edit objects the service applies and cascades into every live instance.",
    }),
    _id: objectIdString.meta({
      description:
        "Forms template _id (ObjectId 24-hex string) of the in-use template to edit; from the formController listing.",
    }),
    formCategory: z.string().optional().meta({
      description: "Form category of the template (e.g. INSPECTION); scopes which live instances receive the cascade.",
    }),
  })
  .meta({
    description:
      "Applies structural edits to an already-in-use Form template (_id) and cascades them into the master Forms doc AND every live (non-deleted/rejected/approved) InspectionAndProgress instance and formFlow that uses it. Requires the EDIT_FORM_TEMPLATES portal permission. The `changes` field is validated by templateChangesSchema — an object keyed by templateEditCases (ADD_QUESTION, EDIT_QUESTION, DELETE_QUESTION, CONFIGURE_QUESTION, EDIT_SECTION_HEADING, CONFIGURE_SECTION, DELETE_SECTION, CHANGE_SECTION_SEQUENCE, CHANGE_QUESTION_SEQUENCE), each key an array of per-case edit objects (add-question/configure-section cases are looseObject so extra builder keys are preserved). Only forms with formIsInUse:true are matched. Use editTemplateSchema for name/type edits on not-yet-used templates. For MAILBOX and ISSUES templates the batch is additionally checked against the two behavioural per-question flag rules (editableInForm/isMetadata not both false; isMandatory not true while editableInForm is false) and rejected with status:false + that rule's errorMessage before any write. The flags-must-be-present rule is NOT applied here — a positional edit carries one flag and the rest are replayed from the stored question, so presence is only required on the whole-template routes. Because these are positional edits, the rules are judged on the state AFTER the batch is replayed onto the stored questions — a single CONFIGURE_QUESTION change carries one flag while the others live in the document, and one batch may legitimately turn isMetadata on and editableInForm off together. Only questions the batch touches are judged, so a pre-existing invalid question cannot block unrelated edits.",
  });

// Controller spreads req.body.value and adds req.body.formId to service
// Service uses: params.formId, params.isArchived, params.resourceType + grid fields
export const templateDefaultSchema = z
  .object({
    action: z
      .string()
      .optional()
      .meta({ description: "Dispatcher action selector; omit for the list/default (read) branch." }),
    formId: z
      .union([objectIdString, z.array(objectIdString)])
      .optional()
      .meta({
        description:
          "Fetch specific Forms TEMPLATE(s) by _id — a single ObjectId 24-hex string or an array of them. Omit to list templates via the grid. Ids come from a prior formController listing.",
      }),
    // Some callers (e.g. getUpdatedTemplate) send isArchived at the top level; the controller
    // reads it off `value`, so a top-level flag is accepted here for type-compatibility.
    isArchived: z.boolean().optional().meta({
      description:
        "Top-level archive filter accepted for compatibility, but the service reads value.isArchived — prefer setting it inside value.",
    }),
    value: gridValueSchema
      .extend({
        // Sortable columns (name, description, type.name, createdDate, createdBy.fullName) are a subset
        // of the filterable allow-list, so reuse it. Overrides the open base `sorted`.
        sorted: sortConfigFor(templatesListFilterableFields, { patterns: dynamicCustomFieldPatterns }),
        where: templatesListWhere.meta({
          description:
            "Grid filter clause over allowed columns (name, description, type.name, createdDate, createdBy.fullName, _id, isArchived, projectId, type._id) plus dynamic custom-field patterns.",
        }),
        resourceType: z
          .string()
          .optional()
          .meta({ description: "Form category to filter templates by (e.g. INSPECTION)." }),
        isArchived: z
          .boolean()
          .optional()
          .meta({ description: "Filter by archive state: true = archived only, false = active only." }),
        projectId: objectIdString.optional().meta({
          description:
            "If set, returns only templates attached to this Project (ObjectId 24-hex string) and enforces the project-level CONFIGURATION_PROJECT_VIEW_LIST_OF_FORM_TEMPLATES permission instead of the portal permission.",
        }),
        includeTemplatesUsedInProject: z.boolean().optional().meta({
          description:
            "Only meaningful together with projectId. When true, the result also contains templates that are no longer attached to the Project but still have (non-deleted) forms created from them in it, each flagged with isRemovedFromProject:true. Used by the form-entries picker so entries of a detached template stay reachable; leave unset for pickers that must only offer currently-attached templates (e.g. form creation).",
        }),
      })
      .optional()
      .meta({
        description:
          "Grid paging/filter envelope: skip, take, sorted, where, requiresCounts plus resourceType/isArchived/projectId/includeTemplatesUsedInProject.",
      }),
  })
  .meta({
    description:
      "Lists Form templates (the Forms collection) for the portal, filtered by resourceType and grid where/sorted/skip/take, or fetches specific template(s) when formId (single id or array) is supplied. If value.projectId is set it returns only templates attached to that Project and enforces the CONFIGURATION_PROJECT_VIEW_LIST_OF_FORM_TEMPLATES project permission; otherwise it enforces the VIEW_LIST_OF_FORM_TEMPLATES portal permission. Add value.includeTemplatesUsedInProject to also get detached-but-still-used templates (flagged isRemovedFromProject). This is the read/list endpoint — use saveTemplate/editTemplate to mutate.",
  });

export function getTemplatesRequestSchema(action?: string) {
  switch (action) {
    case "insert":
      return saveTemplateSchema;
    case "update":
      return editTemplateSchema;
    case "delete":
      return deleteTemplateSchema;
    case "archive":
      return archiveTemplateSchema;
    case "updateUsedTemplate":
      return updateUsedTemplateSchema;
    default:
      return templateDefaultSchema;
  }
}
export const getTemplatesResponse = extendStatusResponse({
  result: z.array(z.unknown()).optional(),
  count: z.number().nonnegative().optional(),
}).meta({
  description: "Response for form controller",
});

// Discriminated union composed from the SAME per-action schemas the dynamicSchema
// dispatcher (getTemplatesRequestSchema) returns — single source of truth. (Historical note: the
// updateUsedTemplate variant's `changes` used to be `z.any()`; it is now the per-templateEditCases
// record of looseObject arrays declared above, so the union is JSON-Schema representable for
// advertising and HTTP validation is unaffected.)

// Plain-object inner schema (no `.refine`) so it can be a `discriminatedUnion` member in
// taskInspectionAndProgressFormsControllerRequestUnion below. `tipfUpdateSchema` derives
// from this and adds the cross-field refinement — single source of truth for the fields.
const tipfUpdateInnerSchema = z.object({
  action: z.literal("update").meta({ description: 'Dispatcher action selector; must be the literal "update".' }),
  _id: objectIdString.optional().meta({
    description:
      "InspectionAndProgress form-INSTANCE _id (ObjectId 24-hex string) to mutate; from the taskInspectionAndProgressFormsController listing. Either _id or formId is required.",
  }),
  formId: objectIdString.optional().meta({
    description:
      "Alternative to _id: the same form-instance identifier. Either _id or formId is required (both accepted).",
  }),
  data: z.object({
    // NOTE: within one request the service executes only the FIRST matching operation (rollback,
    // level-submit, submit, approve, reject, section-verify, then the section-specific handlers).
    // Send fields for a single intended operation per call.
    rollBack: z
      .boolean()
      .optional()
      .meta({ description: "If true, roll the form back one approval level (decrements currentLevelOfForm)." }),
    SES: z.string().optional().meta({
      description:
        "SAP Service Entry Sheet number; when set during final-level section verification it is written to the form's SES_NO field. Only consumed on the verification path.",
    }),
    editedLevel: formFlowLevelSchema.optional().meta({
      description:
        "A modified approval-level definition applied to the next level during submit/approve (dynamic-user reconfiguration).",
    }),
    autoVerifyAllSections: z.boolean().optional().meta({
      description:
        "If true, treat all sections as verified so the form can be approved without per-section verification.",
    }),
    config: z
      .object({
        wbsItemId: objectIdString.meta({
          description: "contractsHasWBSItems item id whose quantity/amount is being set.",
        }),
        boqItemId: objectIdString.optional().meta({ description: "Optional BOQ item id under the WBS item." }),
        quantity: z.number().nonnegative().optional().meta({ description: "Quantity to record for the item." }),
        amount: z.number().nonnegative().optional().meta({ description: "Amount to record for the item." }),
        progressUpdateId: objectIdString
          .optional()
          .meta({ description: "Progress-update context id, when the form is a progress update." }),
        inspectionId: objectIdString
          .optional()
          .meta({ description: "Inspection context id, when the form is an inspection." }),
        remark: zSafeHtml
          .optional()
          .nullable()
          .meta({ description: "Optional sanitized-HTML remark for the item entry." }),
      })
      .optional()
      .meta({
        description:
          "Item-value payload used by the updateItemFields WBS operation; only read on the WBS-items section path.",
      }),
    updateItemFields: z.boolean().optional().meta({
      description:
        "If true (with wbsItemsSectionId + config), updates the WBS item's recorded quantity/amount fields on the form.",
    }),
    attachWBSItemsToForm: z
      .object({
        boqItemIds: z
          .array(objectIdString)
          .optional()
          .meta({ description: "BOQ item ids to attach under the WBS item." }),
        wbsItemId: objectIdString.meta({ description: "contractsHasWBSItems item id to attach to the form." }),
      })
      .optional()
      .meta({
        description: "Attaches a WBS item (and optional BOQ items) to the form; only on the WBS-items section path.",
      }),
    removeWBSItemsFromForm: z
      .object({
        wbsItemIds: z.array(objectIdString).optional().meta({ description: "WBS item ids to detach from the form." }),
      })
      .optional()
      .meta({ description: "Detaches WBS items from the form; only on the WBS-items section path." }),
    reviewsSectionId: z.string().optional().meta({
      description:
        "36-char UUID of the Reviews preset section; presence routes/permission-gates the attach-reviews operation.",
    }),
    attachedReviews: z
      .array(
        z.object({
          _id: objectIdString.meta({ description: "Review _id (ObjectId 24-hex string) to attach to the form." }),
        }),
      )
      .optional()
      .meta({ description: "Reviews to attach to the form (with reviewsSectionId)." }),
    wbsItemsSectionId: z.string().optional().meta({
      description:
        "36-char UUID of the WBS-items preset section; presence routes/permission-gates WBS-item operations.",
    }),
    // SAFE TO TIGHTEN LATER (request-side): this array is written straight through to
    // `selectedForm.template.$[section].tableDataSource` (forms.service.ts manageTableSectionInForm),
    // so its element is the same table ROW as `tableSectionRow` in
    // forms/inspectionAndProgress.collection.ts. cube-qa census of that write target over 4376 stored
    // instance sections: 333 carry the field and every one is an array (array=333; 211 non-empty,
    // 122 empty), and all 1184 elements are plain objects (object=1184, no scalars/arrays/nulls);
    // row keys rowId=1184, 36-char question UUIDs=8074, isPreFilledRow=992.
    tableSectionDataSource: z
      .array(z.unknown())
      .optional()
      .meta({ description: "New rows/grid data for a table section (with tableSectionId)." }),
    tableSectionId: z.string().optional().meta({
      description:
        "36-char UUID of the table section to update; presence + tableSectionDataSource routes the table-update operation.",
    }),
    referencesSectionId: z.string().optional().meta({
      description:
        "36-char UUID of the References preset section; presence routes/permission-gates attaching reference file versions.",
    }),
    mileStoneSectionId: z.string().optional().meta({
      description:
        "36-char UUID of the Milestones preset section; presence routes/permission-gates milestone operations.",
    }),
    mileStoneId: objectIdString.optional().meta({
      description:
        "Target mileStones _id (ObjectId 24-hex string) for updateMilestoneAmount / updateMileStoneDate operations.",
    }),
    completionDate: z.number().nullable().optional().meta({
      description:
        "Epoch ms. DEAD/unread input — no milestone handler consumes the top-level completionDate; milestone completion dates are set only via updateMileStoneDate.completionDate (with mileStoneId).",
    }),
    attachMilestones: z.array(objectIdString).optional().meta({ description: "Milestone _ids to attach to the form." }),
    removeMilestones: z
      .array(objectIdString)
      .optional()
      .meta({ description: "Milestone _ids to detach from the form." }),
    updateMileStoneDate: z
      .object({
        completionDate: z.number().meta({ description: "New completion date as epoch ms." }),
      })
      .optional()
      .meta({ description: "Sets a milestone's completion date (requires mileStoneId)." }),
    updateMilestoneAmount: z
      .object({
        amountInForm: z.number().nonnegative().meta({ description: "Amount recorded for the milestone in this form." }),
      })
      .optional()
      .meta({ description: "Sets a milestone's amount in the form (requires mileStoneId)." }),
    addBoqItems: z
      .object({
        boqItems: z
          .array(
            z.object({
              customQuantity: z
                .number()
                .nonnegative()
                .meta({ description: "Quantity to reserve/record for this BOQ item." }),
              itemId: objectIdString.meta({ description: "contractsHasBoqItems item id to add." }),
            }),
          )
          .meta({ description: "BOQ items to add to the form's Quantities section." }),
        autoPopulateAllAvailableQuantity: z.boolean().meta({
          description: "If true, auto-fills each item's full available quantity instead of using customQuantity.",
        }),
      })
      .optional()
      .meta({ description: "Adds BOQ items to the Quantities preset section (with quantitiesSectionId)." }),
    customQuantityData: z
      .object({
        value: z.number().nonnegative().meta({ description: "The custom quantity value to record." }),
        itemId: objectIdString.meta({ description: "BOQ item id the quantity applies to." }),
        originalTaskId: objectIdString
          .optional()
          .meta({ description: "Task id context when recording quantity against a task." }),
        inspectionId: objectIdString
          .optional()
          .nullable()
          .meta({ description: "Inspection context id, if applicable." }),
        progressUpdateId: objectIdString
          .optional()
          .nullable()
          .meta({ description: "Progress-update context id, if applicable." }),
      })
      .optional()
      .meta({
        description: "Records a custom quantity for a single BOQ item; used on the Quantities and Tasks section paths.",
      }),
    taskConfig: z
      .object({
        originalTaskId: objectIdString.meta({
          description: "contractsHasTasks originalTaskId whose progress fields are being set.",
        }),
        progress: z
          .number()
          .min(0)
          .max(100)
          .optional()
          .nullable()
          .meta({ description: "Percent complete, 0-100; null to clear." }),
        remDuration: z.number().optional().nullable().meta({ description: "Remaining duration; null to clear." }),
        actualEndDate: z
          .number()
          .optional()
          .nullable()
          .meta({ description: "Actual end date as epoch ms; null to clear." }),
        actualStartDate: z
          .number()
          .optional()
          .nullable()
          .meta({ description: "Actual start date as epoch ms; null to clear." }),
      })
      .optional()
      .meta({ description: "Sets progress/dates for one attached task; only on the Tasks section path." }),
    tasksSectionId: z.string().optional().meta({
      description: "36-char UUID of the Tasks preset section; presence routes/permission-gates task operations.",
    }),
    removeTasks: z.array(objectIdString).optional().meta({ description: "Task ids to detach from the form." }),
    attachTasks: z.array(objectIdString).optional().meta({ description: "Task ids to attach to the form." }),
    referenceFileVersionIds: z
      .array(objectIdString)
      .optional()
      .meta({ description: "files_properties file-version ids to attach as references (with referencesSectionId)." }),
    quantitiesSectionId: z.string().optional().meta({
      description:
        "36-char UUID of the Quantities preset section; presence routes/permission-gates BOQ quantity operations.",
    }),
    removeBoqItems: z
      .array(objectIdString)
      .optional()
      .meta({ description: "BOQ item ids to remove from the Quantities section." }),
    isSubmitted: z.boolean().optional().meta({
      description:
        "If true and at the final submission level, submits the form (status SUBMITTED); requires actionComment context.",
    }),
    isRejected: z
      .boolean()
      .optional()
      .meta({ description: "If true, reject the form (status REJECTED, reverts quantities). Requires actionComment." }),
    actionComment: zSafeHtml.optional().meta({
      description:
        "Sanitized-HTML comment stored with a submit/approve/reject/rollback action; required for approve and reject.",
    }),
    actionSignDoc: z.string().optional().nullable().meta({
      description:
        "Signature document: an S3 key/base64 stream, or omitted when actionSignType is IMPORT_PROFILE_SIGNATURE.",
    }),
    actionSignType: z.string().optional().meta({
      description: "Signature mode, e.g. IMPORT_PROFILE_SIGNATURE, controlling how actionSignDoc is handled.",
    }),
    isApproved: z.boolean().optional().meta({
      description:
        "If true (form submitted + all sections verified), approve the form (status APPROVED, commits quantities). Requires actionComment.",
    }),
    sectionsVerified: z.array(z.string()).optional().meta({
      description:
        "Section 36-char UUIDs being marked verified at the final level; drives the section-verification operation.",
    }),
    proceedToNextLevel: z.boolean().optional().meta({
      description: "If true and below the final level, advance the form to the next approval level (level submit).",
    }),
    sectionId: z
      .string()
      .optional()
      .meta({ description: "36-char UUID of the section containing the question being answered (answer-fill path)." }),
    questionId: z
      .string()
      .optional()
      .meta({ description: "36-char UUID of the question being answered (answer-fill path)." }),
    answer: z.unknown().optional().meta({
      description: "The answer value to write for sectionId/questionId; shape depends on the question's fieldType.",
    }),
    addFileUploadFieldData: z
      .object({
        // The check behind this is a lookup in the question's own stored `answer.value`, which the client
        // sends back on every update — so it constrains the key to one the answer already references. It
        // is not, on its own, proof that the caller owns the object; say so rather than promising more
        // than the code does (this text is what an MCP agent reads before passing a key here).
        awsKey: uuidStorageKey.optional().meta({
          description:
            "Only for re-saving an edited image over the object it already occupies. The key must already appear in this question's own stored `answer.value` file list; the server refuses the request otherwise. That list travels back through the client on every update, so this is a consistency check against the stored answer rather than proof that the caller owns the underlying storage object. Omit it on a new upload: the server mints the key and returns it as `addedFileData.awsKey`.",
        }),
        tableRowIndex: z
          .number()
          .optional()
          .meta({ description: "Row index when the fileUpload field is inside a table section." }),
      })
      .optional()
      .meta({
        description:
          "NOT AVAILABLE over MCP or a plain JSON request. Adds a file to a fileUpload question's answer: the bytes must arrive as the request's multipart `file` part (with the JSON body, encoded, as a `payload` part), which JSON cannot carry, so a request without that part is rejected at validation. On a new upload the body carries no storage key at all: the server mints one, appends `{awsKey, name, type}` to the answer itself (name/type read off the multipart part, not the caller), and returns it as `addedFileData` for the client to fold into its local state. Consequently `answer.value` / `tableSectionDataSource` must be sent WITHOUT the new file — send the list as it stands before the upload.",
      }),
    removeFileUploadFieldData: z
      .object({
        tableRowIndex: z
          .number()
          .optional()
          .meta({ description: "Row index when the fileUpload field is inside a table section." }),
        // `.max` bounds the storage round-trips one request can fan out. The handler's own intersection
        // with the question's stored keys is the real guard (it bounds the work by that question's file
        // count whatever the caller sends); this is only the outer limit — see MAX_FILE_KEYS_PER_REMOVAL.
        awsKey: z.array(uuidStorageKey).min(1).max(MAX_FILE_KEYS_PER_REMOVAL).meta({
          description:
            "Storage keys of the files to remove — one entry for a single delete, every key for a 'delete all', up to this array's declared maxItems. Duplicates are collapsed and the list is intersected with this question's own stored `answer.value` file list before any storage call, so a repeated key removes the file once and a key that is not on that list is skipped silently, without an error. That list travels back through the client on every update, so the intersection bounds the request to keys the answer already references rather than proving the caller owns the objects.",
        }),
      })
      .optional()
      .meta({
        description:
          "Removes one or more files from a fileUpload question's answer. The server only unlinks the storage objects and adjusts the form's size — it does not edit the answer. So `answer.value` / `tableSectionDataSource` must be sent WITHOUT the removed files, the mirror of addFileUploadFieldData, where the list is sent without the file being added.",
      }),
    linkFormSectionId: z.string().optional().meta({
      description:
        "36-char UUID of the Linked-Forms preset section; presence routes/permission-gates linking other forms.",
    }),
    linkedForms: z
      .array(linkedFormSchema)
      .optional()
      .meta({ description: "Other form instances to link into this form (with linkFormSectionId)." }),
    attachmentsData: z
      .object({
        name: zPlainTextRequired.meta({ description: "Display file name of the attachment being added." }),
        type: z.string().optional().meta({ description: "MIME type of the attachment." }),
      })
      .optional()
      .meta({ description: "Metadata for an attachment added via addAttachment." }),
    addAttachment: z.boolean().optional().meta({
      description:
        "NOT AVAILABLE over MCP or a plain JSON request. If true, adds the attachment described by attachmentsData to the form: the bytes must arrive as the request's multipart `file` part (with the JSON body, encoded, as a `payload` part), which JSON cannot carry, so a request without that part is rejected at validation. Only a true value routes here — `addAttachment: false` is the marker the removal path sends and passes on either transport.",
    }),
    attachmentIndex: z.number().optional().meta({
      description:
        "Accepted but NOT read by any handler (dead input) — add/remove attachment operations key on addAttachment / removeAttachmentAwsKey, not on an index.",
    }),
    removeAttachmentAwsKey: uuidStorageKey.optional().meta({
      description:
        "Storage key of an existing attachment to remove from the form; always a bare uuid, since every attachment key is minted server-side by addAttachment/uploadFilesInTemplate. The handler requires the key to be present in sectionId/questionId's own attachmentsData and refuses with 'Attachment not found on this question' otherwise — a caller cannot unlink an object it does not own. Unlike removeFileUploadFieldData.awsKey, the server also pulls the entry out of attachmentsData itself, so the caller sends nothing alongside.",
    }),
  }),
});

export const tipfUpdateSchema = tipfUpdateInnerSchema
  .refine(
    (data) => {
      return data._id || data.formId;
    },
    {
      error: "Either _id or formId must be provided",
    },
  )
  .meta({
    description:
      "The multi-purpose mutation for a single form instance (InspectionAndProgress), identified by _id or formId (at least one required). Runs inside a MongoDB transaction. Depending on the fields in `data`, it fills a question answer (sectionId/questionId/answer), verifies sections, submits/approves/rejects/rolls back through the form's approval levels, and attaches/removes WBS items, BOQ items, tasks, milestones, reviews, linked forms, and file uploads. The two operations that consume file bytes — addFileUploadFieldData and addAttachment:true — need the multipart form of the request and are rejected on a JSON or MCP call, which cannot carry a `file` part; every other operation works on either transport. Action-level permission is enforced inline against the instance's selectedForm.template permissions at the current level (approve/reject require actionComment). Get _id/formId from /taskInspectionAndProgressFormsController (tipfDefaultSchema).",
  });

/**
 * `tipfUpdateSchema` as it applies to a request with no `file` part — a JSON POST, or an MCP tool call,
 * which cannot carry one at all. Derived from the SAME inner object so the two variants cannot drift; the
 * route keeps `schema: tipfUpdateSchema` as its advertised input (OpenAPI + the MCP tool shape) and
 * selects this one through `dynamicSchema` when `req.file` is absent.
 *
 * The point is where the rejection happens: without it the two file-consuming operations open a Mongo
 * session, start a transaction and run the permission work before failing on a missing part. `addAttachment:
 * false` is the REMOVE path's marker and must keep passing, so the test is truthiness, not presence.
 */
export const tipfUpdateWithoutFileSchema = tipfUpdateInnerSchema
  .superRefine((payload, ctx) => {
    const fileRequired = "requires the file to be sent as the request's `file` part; not available over JSON or MCP";
    if (payload.data?.addFileUploadFieldData) {
      ctx.addIssue({ code: "custom", path: ["data", "addFileUploadFieldData"], message: fileRequired });
    }
    if (payload.data?.addAttachment) {
      ctx.addIssue({ code: "custom", path: ["data", "addAttachment"], message: fileRequired });
    }
  })
  .refine(
    (data) => {
      return data._id || data.formId;
    },
    {
      error: "Either _id or formId must be provided",
    },
  );

export const tipfSaveSchema = z
  .object({
    action: z.literal("save").meta({ description: 'Dispatcher action selector; must be the literal "save".' }),
    data: z.object({
      title: zPlainTextRequired
        .pipe(z.string().min(1).max(500))
        .meta({ description: "Title of the new form instance (1-500 chars)." }),
      rememberUserSettings: z.boolean().meta({
        description:
          "If true, saves this form's settings under the user's FORM_SETTINGS so it can be quick-created later via createFormFromUserSettings.",
      }),
      description: zSafeHtml
        .pipe(z.string().max(5000))
        .optional()
        .meta({ description: "Optional sanitized-HTML description (max 5000 chars)." }),
      dueDate: z.number().optional().nullable().meta({ description: "Optional due date as epoch ms; null for none." }),
      resourceType: z.string().meta({
        description:
          "Form category (e.g. INSPECTION, PROGRESS_UPDATES); also the space-permission name checked for create rights.",
      }),
      selectedTemplate: objectIdString.meta({
        description:
          "Forms TEMPLATE _id (ObjectId 24-hex string) to instantiate; from the formController listing. Its full template is copied into the new instance.",
      }),
      formFlow: z
        .array(formFlowLevelSchema)
        .meta({ description: "The approval levels (Fill/Modify/Verify grids + allowed users) for this instance." }),
      // SAFE TO TIGHTEN LATER (request-side): both are `z.array(objectIdString)` on the wire — the
      // service consumes them as `obj.attachedProgressUpdates.map((id: string) => new ObjectId(id))`
      // / `obj.attachedInspections.map((id: string) => new ObjectId(id))` (forms.service.ts
      // generateFormDataToInsert), so a non-24-hex string throws BSONError today. The persisted
      // mirror confirms the element type: cube-qa census over 1475 InspectionAndProgress documents —
      // attachedProgressUpdates on 46 docs, always an array (array=46), all 62 elements ObjectId
      // (ObjectId=62, no strings, no nulls); attachedInspections on 16 docs, always an array
      // (array=16), all 19 elements ObjectId (ObjectId=19). Matches
      // forms/inspectionAndProgress.collection.ts, which already types both as
      // `z.array(z.instanceof(ObjectId))`.
      attachedProgressUpdates: z.array(z.unknown()).optional().meta({
        description:
          "Source progress-update form-instance ids (ObjectId strings) this form is derived from. If non-empty, the form becomes basedOnProgressUpdates and quantities are transferred from those forms. Takes precedence over attachedInspections.",
      }),
      attachedInspections: z.array(z.unknown()).optional().meta({
        description:
          "Source inspection form-instance ids (ObjectId strings) this form is derived from. If non-empty (and attachedProgressUpdates is empty), the form becomes basedOnInspections.",
      }),
      spaceId: objectIdString.meta({
        description:
          "Space _id (ObjectId 24-hex string) the form is created in; create-permission is checked here. From getSpacesList.",
      }),
      contractId: objectIdString.optional().meta({
        description:
          "Contract _id (ObjectId 24-hex string) for contract-based forms; requires the contract locked, caller has modify permission, module enabled, and not in draft (except changeOrder). Omit for non-contract forms.",
      }),
      contractVersionId: objectIdString.optional().meta({
        description:
          "On a normal form the persisted contractVersionId is overwritten with the contract's latest version (input does not affect it). BUT on the changeOrder path the raw input IS read to select which contractsVersions doc receives changeOrderId — a wrong value targets the wrong version and omitting it matches none.",
      }),
      basedOnTasks: z.boolean().optional().meta({
        description:
          "IGNORED on save: the service hardcodes basedOnTasks=true then derives it from attachedInspections/attachedProgressUpdates; the input value is not read.",
      }),
      basedOnInspections: z.boolean().optional().meta({
        description:
          "The PERSISTED basedOnInspections is derived server-side (set true when attachedInspections is non-empty), but the INPUT flag IS read: source-form quantity transfer runs only when attachedInspections AND basedOnInspections are both truthy — sending it false/omitting it suppresses the transfer.",
      }),
      basedOnBudget: z.boolean().optional().meta({
        description:
          "IGNORED on save: the service hardcodes basedOnBudget=true unless attached* arrays override it; the input value is not read.",
      }),
      basedOnProgressUpdates: z.boolean().optional().meta({
        description:
          "The PERSISTED basedOnProgressUpdates is derived server-side (set true when attachedProgressUpdates is non-empty), but the INPUT flag IS read: source-form quantity transfer runs only when attachedProgressUpdates AND basedOnProgressUpdates are both truthy — sending it false/omitting it suppresses the transfer.",
      }),
      parentContract: z.string().optional().nullable().meta({
        description:
          "IGNORED on save: derived from the contract (contractData.parentId) for contract forms; the input value is overwritten.",
      }),
      allParentContracts: z.array(objectIdString).optional().meta({
        description:
          "IGNORED on save: derived from the contract (contractData.allParents); the input value is overwritten.",
      }),
      allChildContracts: z.array(objectIdString).optional().meta({
        description:
          "IGNORED on save: derived from the contract (contractData.allChilds); the input value is overwritten.",
      }),
      projectId: objectIdString.meta({
        description: "Project _id (ObjectId 24-hex string) the form belongs to; from the projects listing.",
      }),
      selectedFormFlow: z
        .object({
          name: zPlainTextRequired.meta({
            description: "Name of the reusable form-flow template this instance is based on.",
          }),
          _id: objectIdString.meta({
            description: "formFlow template _id (ObjectId 24-hex string) this instance is based on.",
          }),
        })
        .optional()
        .meta({ description: "Optional reference to the reusable formFlow template used; recorded on the instance." }),
    }),
  })
  .meta({
    description:
      "Creates a new Form instance (an InspectionAndProgress record) in a Space from a selected Form template and its approval formFlow. Enforces Space create-permission for the given resourceType via checkCreatePermissionInSpace; for Contract-based forms it additionally requires the Contract to be locked, the caller to hold contract modify permission, the module enabled, and the contract not in draft (except changeOrder). Requires spaceId (getSpacesList) + projectId (projects_getIncludedProjectsController) + resourceType + selectedTemplate (a Forms template _id). Runs in a MongoDB transaction. Distinct from formFlow save, which stores a reusable flow template, not a live form.",
  });

export const tipfDeleteSchema = z
  .object({
    action: z.literal("delete").meta({ description: 'Dispatcher action selector; must be the literal "delete".' }),
    resourceType: z.string().meta({
      description: "Form category (e.g. INSPECTION) the listed instances belong to; part of the delete match filter.",
    }),
    resourceIds: z.array(objectIdString).meta({
      description:
        "InspectionAndProgress form-INSTANCE _ids (ObjectId 24-hex strings) to soft-delete; from the form list. Only instances created by the calling user are deleted, and only if not referenced as a dependency by other forms.",
    }),
  })
  .meta({
    description:
      "Soft-deletes Form instances by resourceIds (sets isDeleted). Only forms created by the calling user (createdBy._id must match sessionUserId) and matching the given resourceType/portal are deleted, and only if the form is NOT referenced as a dependency by other forms (via attachedInspections/attachedProgressUpdates) — dependency forms are skipped. For Contract-based forms it also releases the reserved BOQ/WBS quantities. Runs in a transaction. resourceIds are InspectionAndProgress form _ids from the form list (taskInspectionAndProgressFormsController default/list action).",
  });

export const tipfArchiveSchema = z
  .object({
    action: z.literal("archive").meta({ description: 'Dispatcher action selector; must be the literal "archive".' }),
    // No `.min(1)`: an empty list is answered by the handler with INSUFFICIENT_PERMISSION rather than a
    // ZodError, keeping the 200-with-errorCode envelope every consumer of this route already handles.
    resourceIds: z.array(objectIdString).meta({
      description:
        "InspectionAndProgress form-INSTANCE _ids (ObjectId 24-hex strings) to (un)archive; from the form list. Each is matched by portal AND by the caller holding RESOURCE_MODIFY on that form; ids failing either are skipped, not rejected. Repeated ids are collapsed before the batch is counted, so sending one twice does not report a partial failure. An empty list archives nothing and returns INSUFFICIENT_PERMISSION rather than a success that wrote nothing.",
    }),
    data: z.object({
      isArchived: z.object({
        status: z.boolean().meta({ description: "Target archive state: true to archive, false to restore." }),
      }),
    }),
  })
  .meta({
    description:
      "Archives or un-archives existing Form instances by writing isArchived.status on each matched InspectionAndProgress record; pass data.isArchived.status=true to archive, false to restore. Matches forms in the caller's portal on which the caller holds RESOURCE_MODIFY. Partial success is reported as errorCode FORM_ARCHIVE_PARTIAL_PERMISSION with status false; a request where no id both existed in your portal and carried RESOURCE_MODIFY returns INSUFFICIENT_PERMISSION (the two cases are not distinguished). Re-archiving a form already in the requested state counts as success. resourceIds are form _ids from the form list. Does not delete — use tipfDeleteSchema for that.",
  });

const tipfUpdatePermissionSchema = z
  .object({
    action: z.literal("updatePermission").meta({
      description:
        'Dispatcher action selector; the literal "updatePermission". (Advertise-only: the live dispatcher does not handle this action.)',
    }),
    role: z.union([z.literal(4), z.literal(6), z.literal(7)]).meta({
      description:
        "Numeric folder-permission role id to assign the user on the form (e.g. 4/6/7 = viewer/editor/admin tiers).",
    }),
    formId: objectIdString.meta({
      description:
        "InspectionAndProgress form-instance _id (ObjectId 24-hex string) whose user permission is being changed.",
    }),
    userId: objectIdString.meta({
      description: "Account _id (ObjectId 24-hex string) of the user whose role on the form is updated.",
    }),
  })
  .meta({
    description: "Payload for task inspection/progress permission update",
  });

export const tipfDefaultSchema = z
  .object({
    resourceId: objectIdString.optional().meta({
      description: "If set, fetch a single form INSTANCE by its _id (ObjectId 24-hex string) instead of listing.",
    }),
    value: z
      .object({
        skip: z.number().nonnegative().optional().meta({ description: "Pagination offset (rows to skip)." }),
        take: z.number().nonnegative().optional().meta({ description: "Pagination page size (max rows)." }),
        sorted: tipfListSorted,
        where: tipfListWhere.meta({
          description:
            "Grid filter clause over allowed columns (counter, title, status, dueDate, space.spaceName, selectedForm.*, createdBy.fullName, lastAction.*, currentLevelOfForm, isArchived.status, isApproved, _id) plus dynamic custom-field patterns.",
        }),
        isActionable: z.boolean().optional().meta({
          description: "If true, restrict to instances the user can currently act on (IN_PROGRESS, unarchived).",
        }),
        resourceType: z.string().optional().meta({ description: "Form category filter (e.g. INSPECTION)." }),
        projectId: objectIdString
          .optional()
          .meta({ description: "Restrict to instances in this Project (ObjectId 24-hex string)." }),
        contractId: objectIdString
          .optional()
          .meta({ description: "Restrict to instances on this Contract (ObjectId 24-hex string)." }),
        selectedProjectIds: z
          .array(objectIdString)
          .optional()
          .meta({ description: "Restrict to instances across multiple Projects (array of ObjectId 24-hex strings)." }),
        requiresCounts: z
          .boolean()
          .optional()
          .meta({ description: "If true, also return the total matching-instance count." }),
        requiresActionableCount: z
          .boolean()
          .optional()
          .meta({ description: "If true, also return the count of instances the user can currently act on." }),
      })
      .optional()
      .meta({ description: "Grid paging/filter envelope for listing form instances." }),
  })
  .meta({
    description:
      "Lists form instances (the InspectionAndProgress collection — actual filled forms, not templates) that the calling user is permitted to view, scoped to the portal and to instances whose users[] includes the session user with RESOURCE_VIEW permission. Supports grid where/sorted/skip/take, resourceType/projectId/contractId filters, isActionable (only IN_PROGRESS, unarchived), and count flags; pass resourceId to fetch one instance by id. This is the read/list endpoint — use updateTaskInspectionAndProgress (tipfUpdate) or save/delete/archive to mutate.",
  });

export function taskInspectionAndProgressFormsControllerRequestSchema(action?: string) {
  switch (action) {
    case "update":
      return tipfUpdateSchema;
    case "save":
      return tipfSaveSchema;
    case "delete":
      return tipfDeleteSchema;
    case "archive":
      return tipfArchiveSchema;
    case "updatePermission":
      return tipfUpdatePermissionSchema;
    default:
      return tipfDefaultSchema;
  }
}

// RESPONSE-ONLY (responseSchema: on /taskInspectionAndProgressFormsController and the four
// update/save/delete/archive routes; never a route `schema:`), so nothing here is parsed at runtime.
// Only the list path fills `result`: `output.result = inspectionData`, the InspectionAndProgress
// aggregate, or `[]` when the match is empty (forms.service.ts:3432/3435) — and the actionable-count
// branch `delete output.result` entirely (forms.service.ts:3424), which is why it is `.optional()`.
// The element is one document shape, `InspectionAndProgressDocument` in
// forms/inspectionAndProgress.collection.ts (cube-qa: 1475 documents, 4376 embedded template
// sections). That file already imports this module's `questionFieldSchema`, so importing the document
// back here would form a module cycle, and re-declaring it inline would duplicate a model that must
// not drift — hence `z.unknown()` for the element. Besides `result`/`count` the update path also
// returns `lastSaved` (every write) and `addedFileData` (the add-file operation only), both declared
// below; the route is a `handler:` route, so nothing checks the handler's return against this — keep
// the two in step by hand.
export const taskInspectionAndProgressFormsControllerResponse = extendStatusResponse({
  result: z.array(z.unknown()).optional(),
  count: z.number().nonnegative().optional(),
  // Declared so the client can branch on the failure reason instead of matching the message text.
  // `extendStatusResponse` puts it on both branches; only the failure branch ever sets it. Archive is the
  // one action whose codes are part of its contract (see `archiveTaskInspectionAndProgress`); any other
  // action may add one, so the description tells clients to branch on the value rather than the action.
  errorCode: z.number().optional().meta({
    description:
      "Numeric errorCodes entry identifying why the action failed; present only when status is false. The archive action sets FORM_ARCHIVE_PARTIAL_PERMISSION (15001) when only some ids were (un)archived and INSUFFICIENT_PERMISSION (7012) when none were. Other actions may carry any entry of packages/helpers/errorCodes.ts — branch on the numeric value, not on the action — and a failure that sets none carries errorMessage alone.",
  }),
  // The entry the add-file path appended to a fileUpload/image answer on this request. The server mints
  // the storage key, so echoing it back is the only way the caller learns it. Derived from
  // `formFileEntrySchema` — the single declaration of the shape — rather than restated here.
  addedFileData: formFileEntrySchema.optional().meta({
    description:
      "The file entry the server appended to a fileUpload/image answer on this request; present only when the request carried addFileUploadFieldData together with a `file` part, and absent on every other action and on failure. Only `awsKey`, `name` and `type` are set on this path — the File-Manager fields of the entry shape never are. Fetch the bytes from /fileSystemServer/getAttachmentFile/:awsKey.",
  }),
  lastSaved: z
    .object({
      timeStamp: z
        .number()
        .nonnegative()
        .meta({ description: "Epoch ms of the last save of any form instance by the user." }),
      userId: objectIdString.meta({ description: "Account _id (ObjectId 24-hex string) of the user who last saved." }),
    })
    .optional(),
}).meta({
  description: "Response for task inspection/progress forms controller",
});
export const saveTaskInspectionAndProgressResponse = z
  .object({
    status: z.boolean(),
    errorMessage: z.string().optional(),
    id: objectIdString.optional().meta({ description: "The _id of the newly created form instance." }),
  })
  .meta({
    description:
      "Result of the 'save' action: creates one form instance and returns its `id`. That id is the whole point of this response — it is the `_id` every follow-up call needs (updateTaskInspectionAndProgress, attachments, the form's deep link), so a caller that creates a form should read it rather than re-querying the list to find what it just made. `status: false` with `errorMessage` on a permission, contract-lock or validation failure, in which case no form was created and `id` is absent.",
  });

export const formFlowUpdateSchema = z
  .object({
    action: z.literal("update").meta({
      description:
        'Must be the literal "update" (retained for request compatibility); the /updateFormFlow endpoint edits the flow in place by _id.',
    }),
    _id: objectIdString.meta({
      description: "formFlow template _id (ObjectId 24-hex string) to update; from the formFlowController listing.",
    }),
    data: z.object({
      name: zPlainText.optional().meta({ description: "New flow name (patched only if provided)." }),
      projectId: objectIdString.meta({
        description:
          "Project _id (ObjectId 24-hex string) the flow belongs to; the SPECIFIC_CREATE_FORM_FLOW permission is checked against it. From the projects listing.",
      }),
      formFlow: z
        .array(formFlowLevelSchema)
        .meta({ description: "The approval levels (Fill/Modify/Verify grids + allowed users) defining the flow." })
        .optional(),
    }),
  })
  .meta({
    description:
      "Updates an existing reusable form-flow template (formFlow collection) identified by _id, patching only the supplied fields (name, formFlow levels, and dotted selectedForm.template/name/description keys). Requires the SPECIFIC_CREATE_FORM_FLOW project-level permission (checkProjectLevelPermission on data.projectId). A dedicated update endpoint (formsService.updateFormFlow) that edits in place by _id; formFlowSaveSchema (/saveFormFlow) inserts a new flow. _id from the formFlow list (formFlowController); projectId from projects_getIncludedProjectsController.",
  });

export const formFlowSaveSchema = z.object({
  action: z.literal("save").meta({
    description:
      'Must be the literal "save" (retained for request compatibility); the /saveFormFlow endpoint always inserts a new flow.',
  }),
  data: z.object({
    name: zPlainTextRequired.meta({ description: "Name of the new form-flow template." }),
    formCategory: z
      .string()
      .meta({ description: "Form category the flow is for (e.g. INSPECTION, PROGRESS_UPDATES)." }),
    selectedTemplate: objectIdString,
    spaceId: objectIdString.meta({
      description: "Space _id (ObjectId 24-hex string); caller must be a member. From getSpacesList.",
    }),
    projectId: objectIdString.meta({
      description:
        "Project _id (ObjectId 24-hex string); SPECIFIC_CREATE_FORM_FLOW is checked against it. From the projects listing.",
    }),
    userSelectionMode: z.enum(["allSpaceUsers", "currentUser"]).optional(),
    formFlow: z
      .array(formFlowLevelSchema)
      .optional()
      .meta({ description: "The approval levels (Fill/Modify/Verify grids + allowed users) defining the flow." }),
  }),
});

export const formFlowDuplicateSchema = z
  .object({
    action: z.literal("duplicate").meta({
      description:
        'Dispatcher action selector; must be the literal "duplicate" (always duplicates an existing form flow).',
    }),
    sourceFormFlowId: objectIdString.meta({
      description:
        "If duplicating from an existing flow, its _id (ObjectId 24-hex string); that parent's noOfDuplicates counter is incremented. Omit for a brand-new flow.",
    }),
  })
  .meta({
    description: "Duplicates the exising formFlow",
  });

export const formFlowDeleteSchema = z
  .object({
    action: z.literal("delete").meta({ description: 'Dispatcher action selector; must be the literal "delete".' }),
    formFlowIds: z.array(objectIdString).meta({
      description:
        "formFlow template _ids (ObjectId 24-hex strings) to soft-delete; from the formFlowController listing.",
    }),
    projectId: objectIdString.meta({
      description:
        "Project _id (ObjectId 24-hex string) scoping the delete; SPECIFIC_CREATE_FORM_FLOW is checked against it. From the projects listing.",
    }),
  })
  .meta({
    description:
      "Soft-deletes reusable form-flow templates by formFlowIds, scoped to the given projectId (sets isDeleted on matching formFlow docs). Requires the SPECIFIC_CREATE_FORM_FLOW project permission. After deletion, for each referenced Form template it clears Forms.formIsInUse when no other live formFlow or InspectionAndProgress form still uses that template. Targets flow templates, not live form instances (that is tipfDeleteSchema). formFlowIds from the formFlow list; projectId from projects_getIncludedProjectsController.",
  });

export const formFlowAddToLibrarySchema = z
  .object({
    action: z
      .literal("addToLibrary")
      .meta({ description: 'Dispatcher action selector; must be the literal "addToLibrary".' }),
    formFlowIds: z.array(objectIdString).meta({
      description:
        "formFlow template _ids (ObjectId 24-hex strings) in the caller's portal to copy into the Library as FORM_FLOW entries; from the formFlowController listing. Flows missing a project/space are skipped.",
    }),
  })
  .meta({
    description:
      "Copies existing form-flow templates (by formFlowIds, within the caller's portal) into the reusable Library collection as FORM_FLOW entries, upserting one Library record per flow (keyed by data.formFlowId) capturing its selectedForm, formCategory, formFlow, project and space. No explicit role/permission check beyond portal scoping. Flows missing a project/space are skipped. formFlowIds from the formFlow list. Pair with formFlowAddFromLibrarySchema to instantiate library flows back into a Space.",
  });

export const formFlowAddFromLibrarySchema = z
  .object({
    action: z
      .literal("addFromLibrary")
      .meta({ description: 'Dispatcher action selector; must be the literal "addFromLibrary".' }),
    usersData: z
      .array(
        z.object({
          id: objectIdString.meta({
            description:
              "Account _id (ObjectId 24-hex string) to place into every level's allowed submit/approve/reject/rollback user lists.",
          }),
          name: zPlainTextRequired.meta({ description: "Display name of the user (denormalized into the flow)." }),
        }),
      )
      .meta({
        description:
          "Users that REPLACE each instantiated level's allowed-users lists; the library flow's original users are discarded.",
      }),
    formFlowIds: z.array(objectIdString).meta({
      description:
        "Library FORM_FLOW entry _ids (ObjectId 24-hex strings) to instantiate — here these are Library document _ids (from getEntitesFromLibrary), not live formFlow ids.",
    }),
    spaceId: objectIdString.meta({
      description: "Target Space _id (ObjectId 24-hex string); caller must be a member. From getSpacesList.",
    }),
    projectId: objectIdString.meta({
      description:
        "Target Project _id (ObjectId 24-hex string); SPECIFIC_CREATE_FORM_FLOW is checked against it. From the projects listing.",
    }),
  })
  .meta({
    description:
      "Instantiates one or more Library FORM_FLOW entries (by formFlowIds, which here are Library document _ids) into a target Space/Project as new formFlow templates, replacing each level's allowed submit/approve/reject/rollback users with the supplied usersData. Requires the SPECIFIC_CREATE_FORM_FLOW project permission AND that the caller belongs to the Space. spaceId from getSpacesList, projectId from projects_getIncludedProjectsController, usersData ids are account _ids. Reverse of formFlowAddToLibrarySchema.",
  });

export const formFlowArchiveSchema = z
  .object({
    action: z.literal("archive").meta({ description: 'Dispatcher action selector; must be the literal "archive".' }),
    formFlowIds: z.array(objectIdString).meta({
      description:
        "formFlow template _ids (ObjectId 24-hex strings) to (un)archive; from the formFlowController listing.",
    }),
    isArchived: z.boolean().meta({
      description: "Target archive state written to formFlow.isArchived: true to archive, false to unarchive.",
    }),
    projectId: objectIdString.meta({
      description:
        "Project _id (ObjectId 24-hex string) scoping the operation; SPECIFIC_CREATE_FORM_FLOW is checked against it. From the projects listing.",
    }),
  })
  .meta({
    description:
      "Archives or unarchives reusable form-flow templates (formFlow collection) by setting formFlow.isArchived to the boolean isArchived flag for the given formFlowIds scoped to projectId. Requires the SPECIFIC_CREATE_FORM_FLOW project permission. This toggles a flow's archive state only; it does not delete (see formFlowDeleteSchema). formFlowIds from the formFlow list; projectId from projects_getIncludedProjectsController.",
  });

// Service uses: params.formFlowId, params.value.{sorted, projectId, where, spaceId, selectedFormId, formCategory, skip, take, requiresCounts}
export const formFlowDefaultSchema = z
  .object({
    action: z
      .string()
      .optional()
      .meta({ description: "Dispatcher action selector; omit for the list/default (read) branch." }),
    projectId: objectIdString.optional().meta({
      description: "Top-level Project filter (ObjectId 24-hex string); the service also reads value.projectId.",
    }),
    formFlowId: objectIdString.optional().meta({
      description: "If set, fetch a single formFlow template by _id (ObjectId 24-hex string) instead of listing.",
    }),
    value: gridValueSchema
      .extend({
        // Sortable columns (name, selectedForm, space.spaceName, createdBy, createdDate) are a subset
        // of the filterable allow-list, so reuse it. Overrides the open base `sorted`.
        sorted: sortConfigFor(formFlowListFilterableFields, { patterns: dynamicCustomFieldPatterns }),
        where: formFlowListWhere.meta({
          description:
            "Grid filter clause over allowed columns (name, selectedForm, space.spaceName, createdBy, createdDate, _id) plus dynamic custom-field patterns.",
        }),
        projectId: objectIdString
          .optional()
          .meta({ description: "Restrict flows to this Project (ObjectId 24-hex string)." }),
        spaceId: objectIdString
          .optional()
          .meta({ description: "Restrict flows to this Space (ObjectId 24-hex string). From getSpacesList." }),
        selectedFormId: z
          .string()
          .optional()
          .meta({ description: "Restrict flows built on this Forms template _id (ObjectId 24-hex string)." }),
        formCategory: z
          .string()
          .optional()
          .meta({ description: "Restrict flows to this form category (e.g. INSPECTION)." }),
        isArchived: z.boolean().optional().meta({
          description:
            "Archive filter: true = archived flows only; omitted/false = active (non-archived) flows only. Legacy flows without the field are treated as active.",
        }),
      })
      .optional()
      .meta({ description: "Grid paging/filter envelope for listing form-flow templates." }),
  })
  .meta({
    description:
      "Lists reusable form-flow templates (formFlow collection) for the portal, scoped to Spaces the caller belongs to; supports skip/take/sorted/where paging plus optional projectId, spaceId, selectedFormId and formCategory filters. If formFlowId is supplied it returns that single flow document instead. This is read-only; use formFlow save/update to create or modify a flow. projectId from projects_getIncludedProjectsController, spaceId from getSpacesList.",
  });

export function formFlowControllerRequestSchema(action?: string) {
  switch (action) {
    case "update":
      return formFlowUpdateSchema;
    case "save":
      return formFlowSaveSchema;
    case "delete":
      return formFlowDeleteSchema;
    case "addToLibrary":
      return formFlowAddToLibrarySchema;
    case "addFromLibrary":
      return formFlowAddFromLibrarySchema;
    case "archive":
      return formFlowArchiveSchema;
    default:
      return formFlowDefaultSchema;
  }
}

// RESPONSE-ONLY (responseSchema: on /formFlowController and the seven formFlow mutation routes;
// never a route `schema:`), so nothing here is parsed at runtime.
// The list path sets `output.result = formFlowData`, the `formFlow` aggregate
// (forms.service.ts:6617). The element is one document shape, `formFlowDocument` in
// forms/forms.collection.ts — cube-qa: 269 documents, keys _id:ObjectId/name:string/
// selectedForm:object/formCategory:string/formFlow:array/createdDate/updatedDate/portalId/createdBy/
// updatedBy/project=269 each, isDeleted=268, space=268, isArchived=267, noOfDuplicates=257,
// projectId/spaceId=250, resourceType=15, position=1. forms.collection.ts imports
// `formTemplateFieldSchema` FROM this file, so importing the document back here would form a module
// cycle and re-declaring it inline would duplicate a model that must not drift — hence `z.unknown()`.
//
// KNOWN BUG (observed, deliberately NOT fixed here): the single-`_id` read path returns a NON-array
// `result` — `output.result = formFlowData || {}` (forms.service.ts:6562) yields the one matched
// `formFlow` document, or `{}` when the id matches nothing. So `result` is an object on that branch
// while this schema (and the MCP output shape derived from it) advertises an array. `result` is not
// widened to a union here because the sole consumer reads `result.length` and `result[0]`
// (web useCreateInspection.tsx fetchFormFlows), which a union would break.
export const formFlowControllerResponse = extendStatusResponse({
  result: z.array(z.unknown()).optional(),
  count: z.number().nonnegative().optional(),
}).meta({
  description: "Response for form flow controller",
});

// Discriminated union composed from the SAME per-action schemas the dynamicSchema
// dispatcher (formFlowControllerRequestSchema) returns — single source of truth. Every
// explicit variant already pins `action` via z.literal(...); the default (no/unknown
// action) variant is selected via a preprocess that maps any value outside the explicit
// action set to the "__default__" sentinel.

export const projectTemplateConfigHandlerRequest = z
  .object({
    action: z.enum(["add", "remove"]).meta({
      description:
        '"add" attaches templates to the project (requires CONFIGURATION_PROJECT_SELECT_FORM_TEMPLATES); "remove" detaches them (requires CONFIGURATION_PROJECT_REMOVE_FORM_TEMPLATES).',
    }),
    projectId: objectIdString.meta({
      description: "Project _id (ObjectId 24-hex string) to attach/detach templates for; from the projects listing.",
    }),
    templateIds: z.array(objectIdString).meta({
      description:
        "Forms TEMPLATE _ids (ObjectId 24-hex strings) to add to / remove from the project's usable templates; from the formController listing.",
    }),
  })
  .meta({
    description:
      "Attaches or detaches Form templates to/from a Project: action 'add' pushes a {id, noOfFormsOfThisTemplate:0} entry onto each Forms doc's projects[], action 'remove' pulls it. templateIds are Forms template _ids; projectId is the Project _id. Enforces project-level permission CONFIGURATION_PROJECT_SELECT_FORM_TEMPLATES for add and CONFIGURATION_PROJECT_REMOVE_FORM_TEMPLATES for remove. This configures which templates a Project can use; it does not create form instances.",
  });

export const projectTemplateConfigHandlerResponse = formStatusResponse.meta({
  description: "Response for project template config handler",
});

export const getFormWBSItemsAndAssociatedBoqItemsRequest = z
  .object({
    formId: objectIdString.meta({
      description:
        "InspectionAndProgress form-INSTANCE _id (ObjectId 24-hex string), from fetchFormEntries; the form whose WBS+BOQ items are returned.",
    }),
    contractId: objectIdString.meta({
      description:
        "Parent Contract _id (ObjectId 24-hex string); RESOURCE_VIEW on it is required. The latest version is resolved automatically.",
    }),
    contractVersionId: objectIdString.optional().meta({
      description:
        "Optional contract version _id; the service resolves the contract's latest version, so this is not required.",
    }),
  })
  .meta({
    description:
      "For a single contract-based form instance, returns the WBS items attached to that form together with their associated BOQ items (used when the form is basedOnBudget). formId is the InspectionAndProgress instance _id (from fetchFormEntries) and contractId is the parent Contract _id; the contract's latest version is resolved automatically. Requires RESOURCE_VIEW on the Contract. Differs from getFormBoqItems (BOQ only) and getFormTasks (task-based forms).",
  });

export const getFormWBSItemsAndAssociatedBoqItemsResponse = formStatusResponse.meta({
  description: "Response for get form WBS items and associated BOQ items",
});

export const getFormsDataForChartRequest = z
  .object({
    contractId: objectIdString.meta({
      description: "Contract _id (ObjectId 24-hex string) to aggregate form counts for; RESOURCE_VIEW required.",
    }),
    contractVersionId: objectIdString.meta({
      description: "The specific Contract version _id (ObjectId 24-hex string) whose form instances are counted.",
    }),
  })
  .meta({
    description:
      "Returns aggregated counts of form instances for one Contract version, grouped by resourceType (form category) and status, for the contract overview chart — NOT the data of any single form. contractId is the Contract _id and contractVersionId is that contract's version _id. Requires RESOURCE_VIEW on the Contract.",
  });

export const getFormsDataForChartResponse = extendStatusResponse({
  result: z.unknown().optional(),
  count: z.number().nonnegative().optional(),
  data: z.unknown().optional(),
});

// Service only uses: formId (gets contractVersionId from form data)
export const getFormTasksRequest = z
  .object({
    formId: objectIdString.meta({
      description:
        "InspectionAndProgress form-INSTANCE _id (ObjectId 24-hex string) of a task-based contract form; from fetchFormEntries.",
    }),
    contractVersionId: objectIdString.optional().meta({
      description:
        "IGNORED by the service: the contract and version are read from the form document, so this input is not used.",
    }),
  })
  .meta({
    description:
      "For a task-based contract form instance, returns the schedule tasks attached to the form and their associated BOQ items, aggregated by originalTaskId. Only formId (the InspectionAndProgress instance _id) is needed — the contractId and version are read from the form document; contractVersionId in the payload is ignored. Requires RESOURCE_VIEW on the Contract. Use for basedOnTasks forms; use getFormWBSItems for budget/WBS forms.",
  });

export const getFormTasksResponse = formStatusResponse.meta({
  description: "Response for get form tasks",
});

export const getFormBoqItemsRequest = z
  .object({
    formId: objectIdString.meta({
      description:
        "InspectionAndProgress form-INSTANCE _id (ObjectId 24-hex string) of a contract form; from fetchFormEntries. The contract and version are derived from the form document. RESOURCE_VIEW on the resolved contract is required.",
    }),
  })
  .meta({
    description:
      "Returns the BOQ items attached to one contract form instance (in the response's boqItems array). Only formId (the InspectionAndProgress instance _id, from fetchFormEntries) is required — the contractId and version are derived from the form document. Requires RESOURCE_VIEW on the resolved Contract; returns an error if the form does not exist. Narrower than getFormWBSItems, which also returns WBS grouping.",
  });

export const getFormBoqItemsResponse = extendStatusResponse({
  result: z.unknown().optional(),
  count: z.number().nonnegative().optional(),
  boqItems: z.array(z.record(z.string(), z.unknown())).optional(),
});

export const getContractTaskHierarchyRequest = z
  .object({
    contractId: objectIdString.meta({
      description:
        "Contract _id (ObjectId 24-hex string) the task belongs to; RESOURCE_VIEW required (checked via getContractDetails).",
    }),
    originalTaskId: objectIdString.meta({
      description:
        "The task's originalTaskId (ObjectId 24-hex string) within the contract; the tree is built around this task and it is flagged isSelected.",
    }),
  })
  .meta({
    description:
      "Read-only: returns the task ancestry/descendant tree for one contract task, aggregating contractsHasTasks where allChilds or originalTaskId matches the given task; each returned node carries hasChild/parentId/WBSCode/title and an isSelected flag marking the requested task. Requires RESOURCE_VIEW permission on the contract (checked via getContractDetails). Inputs: contractId (a Contract's id) and originalTaskId (the task's originalTaskId within that Contract).",
  });

export const getContractTaskHierarchyResponse = formStatusResponse.meta({
  description: "Response for get contract task hierarchy",
});

export const formUserSettingsControllerRequest = z
  .object({
    action: z.string().meta({
      description:
        "Legacy dispatcher action selector (check/createForm/deleteFormUserSettings); the split endpoints omit it.",
    }),
    projectId: objectIdString.meta({
      description: "Project _id (ObjectId 24-hex string) the saved settings belong to; from the projects listing.",
    }),
    resourceType: z.string().meta({
      description: "Form category string (e.g. INSPECTION, PROGRESS_UPDATES) keying the FORM_SETTINGS entry.",
    }),
  })
  .meta({
    description: "Payload for form user settings controller",
  });

// Per-action payload schemas for formUserSettingsController. The controller dispatches on
// req.body.action across checkFormUserSettings / createForm / deleteFormUserSettings and a
// no-op default; every branch's service (checkFormUserSettings, createFormFromUserSettings,
// deleteFormUserSettings) reads the SAME body fields — projectId + resourceType — so all
// variants share identical non-action fields (mirrors the flat schema exactly).
export const formUserSettingsCheckSchema = z
  .object({
    projectId: objectIdString.meta({
      description:
        "Project _id (ObjectId 24-hex string) to check for a saved FORM_SETTINGS entry; from the projects listing.",
    }),
    resourceType: z
      .string()
      .meta({ description: "Form category string (e.g. INSPECTION) keying the settings entry to check." }),
  })
  .meta({
    description:
      "Read-only: checks whether the calling user has a saved 'remember settings' entry (user_settings key FORM_SETTINGS) for the given Project and form resourceType; returns status plus isUserSettingExists (true/false). Does not create anything. Inputs: projectId (from the projects list) and resourceType (a form category string such as INSPECTION or PROGRESS_UPDATES). Use before createFormFromUserSettings to decide if quick-create is available.",
  });

export const formUserSettingsCreateSchema = z
  .object({
    projectId: objectIdString.meta({
      description:
        "Project _id (ObjectId 24-hex string) whose saved FORM_SETTINGS form is duplicated; from the projects listing.",
    }),
    resourceType: z
      .string()
      .meta({ description: "Form category string (e.g. INSPECTION) keying which saved setting to quick-create from." }),
  })
  .meta({
    description:
      "Write (transactional): quick-creates a NEW form instance by duplicating the InspectionAndProgress form referenced in the caller's saved FORM_SETTINGS for this Project+resourceType — copies title (suffixed '(n)'), description, template, form flow (action state stripped), due date and attachments, then increments the source form's noOfDuplicatedOfThisForm. Fails with 'Settings does not exists' if no saved setting exists; space create permission is enforced inside the underlying save. Inputs: projectId and resourceType (same pair used in checkFormUserSettings). Unlike a raw form save, this reuses the user's remembered form as the template.",
  });

export const formUserSettingsDeleteSchema = z
  .object({
    projectId: objectIdString.meta({
      description:
        "Project _id (ObjectId 24-hex string) whose saved FORM_SETTINGS entry is deleted; from the projects listing.",
    }),
    resourceType: z
      .string()
      .meta({ description: "Form category string (e.g. INSPECTION) keying which saved setting to delete." }),
  })
  .meta({
    description:
      "Write: deletes the calling user's saved 'remember settings' entry (user_settings FORM_SETTINGS) for the given Project and form resourceType, so quick-create is no longer offered for that combination. Inputs: projectId (from the projects list) and resourceType (form category string). Only affects the current user's own settings, not the underlying forms.",
  });

// The default (no-/unknown-action) branch is a controller no-op, but the flat schema still
// required projectId + resourceType, so the default variant keeps the same required fields to
// stay HTTP-equivalent.
const formUserSettingsDefaultSchema = z
  .object({
    projectId: objectIdString.meta({
      description:
        "Project _id (ObjectId 24-hex string); required for HTTP-equivalence with the split endpoints. The default branch is a no-op.",
    }),
    resourceType: z
      .string()
      .meta({ description: "Form category string; required for HTTP-equivalence. The default branch is a no-op." }),
  })
  .meta({ description: "Payload for form user settings controller (default)" });

// Discriminated union composed from the per-action schemas above — single source of truth that
// validates HTTP and lets the MCP bridge auto-expand one tool per action. A preprocess maps any
// value outside the explicit action set to the "__default__" sentinel (the no-op branch).

export const formUserSettingsControllerResponse = formStatusResponse.meta({
  description: "Response for form user settings controller",
});

// ============================================================================
// Type Exports - Single Source of Truth for Frontend Consumption
// ============================================================================

// Add Section to Library Types
export type AddSectionToLibraryRequest = z.infer<typeof addSectionToLibraryRequest>;
export type AddSectionToLibraryResponse = z.infer<typeof addSectionToLibraryResponse>;

// Delete Sections from Library Types
export type DeleteSectionsFromLibraryRequest = z.infer<typeof deleteSectionsFromLibraryRequest>;
export type DeleteSectionsFromLibraryResponse = z.infer<typeof deleteSectionsFromLibraryResponse>;

// Fetch Form Entries Types
export type FetchFormEntriesRequest = z.infer<typeof fetchFormEntriesRequest>;
export type FetchFormEntriesResponse = z.infer<typeof fetchFormEntriesResponse>;

// Form User Settings Controller Types
export type FormUserSettingsControllerRequest = z.infer<typeof formUserSettingsControllerRequest>;
export type FormUserSettingsControllerResponse = z.infer<typeof formUserSettingsControllerResponse>;

// Get Contract Form BOQ Items Types
export type GetContractFormBoqItemsRequest = z.infer<typeof getContractFormBoqItemsRequest>;
export type GetContractFormBoqItemsResponse = z.infer<typeof getContractFormBoqItemsResponse>;

// Get Contract Task Hierarchy Types
export type GetContractTaskHierarchyRequest = z.infer<typeof getContractTaskHierarchyRequest>;
export type GetContractTaskHierarchyResponse = z.infer<typeof getContractTaskHierarchyResponse>;

// Get Entities from Library Types
export type GetEntitesFromLibraryRequest = z.infer<typeof getEntitesFromLibraryRequest>;
export type GetEntitesFromLibraryResponse = z.infer<typeof getEntitesFromLibraryResponse>;

// Get Form BOQ Items Types
export type GetFormBoqItemsRequest = z.infer<typeof getFormBoqItemsRequest>;
export type GetFormBoqItemsResponse = z.infer<typeof getFormBoqItemsResponse>;

// Get Form Tasks Types
export type GetFormTasksRequest = z.infer<typeof getFormTasksRequest>;
export type GetFormTasksResponse = z.infer<typeof getFormTasksResponse>;

// Get Form WBS Items and Associated BOQ Items Types
export type GetFormWBSItemsAndAssociatedBoqItemsRequest = z.infer<typeof getFormWBSItemsAndAssociatedBoqItemsRequest>;
export type GetFormWBSItemsAndAssociatedBoqItemsResponse = z.infer<typeof getFormWBSItemsAndAssociatedBoqItemsResponse>;

// Get Forms Data for Chart Types
export type GetFormsDataForChartRequest = z.infer<typeof getFormsDataForChartRequest>;
export type GetFormsDataForChartResponse = z.infer<typeof getFormsDataForChartResponse>;

// Project Template Config Handler Types
export type ProjectTemplateConfigHandlerRequest = z.infer<typeof projectTemplateConfigHandlerRequest>;
export type ProjectTemplateConfigHandlerResponse = z.infer<typeof projectTemplateConfigHandlerResponse>;

// Form Types Controller Types
export type GetTemplateTypesResponse = z.infer<typeof getTemplateTypesResponse>;

// Form Types Controller Request — individual action variants
export type InsertTemplateTypeRequest = z.infer<typeof insertTemplateTypeSchema>;
export type UpdateTemplateTypeRequest = z.infer<typeof updateTemplateTypeSchema>;
export type DeleteTemplateTypeRequest = z.infer<typeof deleteTemplateTypeSchema>;
export type TemplateTypesDefaultRequest = z.infer<typeof templateTypesDefaultSchema>;

// Form Types Controller Request — discriminated union of all action variants
export type GetTemplateTypesRequest =
  InsertTemplateTypeRequest | UpdateTemplateTypeRequest | DeleteTemplateTypeRequest | TemplateTypesDefaultRequest;

// Form Controller Types
export type GetTemplatesResponse = z.infer<typeof getTemplatesResponse>;

// Form Controller Request — individual action variants
export type SaveTemplateRequest = z.infer<typeof saveTemplateSchema>;
export type EditTemplateRequest = z.infer<typeof editTemplateSchema>;
export type DeleteTemplateRequest = z.infer<typeof deleteTemplateSchema>;
export type ArchiveTemplateRequest = z.infer<typeof archiveTemplateSchema>;
export type UpdateUsedTemplateRequest = z.infer<typeof updateUsedTemplateSchema>;
export type TemplateDefaultRequest = z.infer<typeof templateDefaultSchema>;

// Form Controller Request — discriminated union of all action variants
export type GetTemplatesRequest =
  | SaveTemplateRequest
  | EditTemplateRequest
  | DeleteTemplateRequest
  | ArchiveTemplateRequest
  | UpdateUsedTemplateRequest
  | TemplateDefaultRequest;

// Task Inspection and Progress Forms Controller Types
export type TaskInspectionAndProgressFormsControllerResponse = z.infer<
  typeof taskInspectionAndProgressFormsControllerResponse
>;

export type SaveTaskInspectionAndProgressResponse = z.infer<typeof saveTaskInspectionAndProgressResponse>;

// Task Inspection and Progress Forms Controller Request — individual action variants
export type TipfUpdateRequest = z.infer<typeof tipfUpdateSchema>;
// The two file-operation payloads of `tipfUpdateSchema`, named so `manageFileUploads` can take the wire
// shape instead of restating it. Derived, not declared: the members are what the route validated.
// NOTE (packages/server has strictNullChecks:false): neither the optional markers nor `awsKey`'s min/max
// mean anything inside the service — they hold only because the Zod validation ran at the route first, so
// a non-route caller of those handlers gets no guarantee from these types.
export type TipfAddFileUploadFieldData = NonNullable<TipfUpdateRequest["data"]["addFileUploadFieldData"]>;
export type TipfRemoveFileUploadFieldData = NonNullable<TipfUpdateRequest["data"]["removeFileUploadFieldData"]>;
export type TipfSaveRequest = z.infer<typeof tipfSaveSchema>;
export type TipfDeleteRequest = z.infer<typeof tipfDeleteSchema>;
export type TipfArchiveRequest = z.infer<typeof tipfArchiveSchema>;
export type TipfUpdatePermissionRequest = z.infer<typeof tipfUpdatePermissionSchema>;
export type TipfDefaultRequest = z.infer<typeof tipfDefaultSchema>;

// Task Inspection and Progress Forms Controller Request — discriminated union of all action variants
export type TaskInspectionAndProgressFormsControllerRequest =
  | TipfUpdateRequest
  | TipfSaveRequest
  | TipfDeleteRequest
  | TipfArchiveRequest
  | TipfUpdatePermissionRequest
  | TipfDefaultRequest;

// Form Flow Controller Types
export type FormFlowControllerResponse = z.infer<typeof formFlowControllerResponse>;

// Form Flow Controller Request — individual action variants
export type FormFlowUpdateRequest = z.infer<typeof formFlowUpdateSchema>;
export type FormFlowSaveRequest = z.infer<typeof formFlowSaveSchema>;
export type FormFlowDuplicateRequest = z.infer<typeof formFlowDuplicateSchema>;
export type FormFlowDeleteRequest = z.infer<typeof formFlowDeleteSchema>;
export type FormFlowAddToLibraryRequest = z.infer<typeof formFlowAddToLibrarySchema>;
export type FormFlowAddFromLibraryRequest = z.infer<typeof formFlowAddFromLibrarySchema>;
export type FormFlowArchiveRequest = z.infer<typeof formFlowArchiveSchema>;
export type FormFlowDefaultRequest = z.infer<typeof formFlowDefaultSchema>;

// Form Flow Controller Request — discriminated union of all action variants
export type FormFlowControllerRequest =
  | FormFlowUpdateRequest
  | FormFlowSaveRequest
  | FormFlowDuplicateRequest
  | FormFlowDeleteRequest
  | FormFlowAddToLibraryRequest
  | FormFlowAddFromLibraryRequest
  | FormFlowArchiveRequest
  | FormFlowDefaultRequest;
